// ===== CONFIG =====
import {
  escapeHtml, escapeRegExp, looksLikeHtml, stripHtml, toPlainText, sanitizeHtml,
  cssEscapeIdent, humanizeIdentifier,
  formatDateTimeShort, formatDateTimeFull, formatRelativeTime,
  formatLocalDayKey, formatResolutionDayLabel,
  formatCurrency, formatNumber,
  loadFormattingConfig,
  parseListInput, normalizeStringList,
  debounce, filterTable,
} from './src/lib/format.js';
import {
  hasRole, canAccessTicketing, canCreateTickets, hasCustomerBoardAccess,
  ROLE_LABELS, ROLE_HELP_TEXT, ROLE_DISPLAY_ORDER,
  setCurrentUserGetter as setRolesCurrentUserGetter,
} from './src/lib/roles.js';
import {
  readThemeCookie, getThemePreference, setThemePreference,
  updateThemeToggle, applyTheme, ensureThemeInitialized,
} from './src/lib/theme.js';
import {
  configureAuth, installAuthListeners,
  updateSessionStatus, updateProfileMenuLabel, closeProfileMenu, openProfileDialog,
  gate, performLogout, handleLoginSubmit, consumeResetTokenFromUrl,
} from './src/lib/auth.js';
import { openDialog, closeDialog } from './src/lib/dialog.js';
import {
  CHANNEL_OPTIONS, CHANNEL_LABEL_MAP, getChannelLabel,
  PBX_EVENT_OPTIONS, DEFAULT_PBX_EVENTS,
} from './src/lib/channels.js';
import { isValidTimeString, normalizePbxConfig } from './src/lib/pbx.js';
import {
  getTechnicianAdminUsers, getSupervisorAdminUsers, getTechSupervisorAdminUsers,
  getTechnicianRosterState, setTechnicianRosterState as setRosterStateThunk,
  getTechnicianProfileByAdminId,
  ensureTechnicianUsersLoaded, ensureSupervisorUsersLoaded,
  ensureTechSupervisorUsersLoaded, ensureTechnicianRosterLoaded,
  updateTechnicianProfiles,
} from './src/lib/admin-roster.js';
import { setViewRouter } from './src/lib/view-router.js';
import { notificationRecent, queueTicketNotification } from './src/lib/ticket-notifications.js';
import * as adminAPI from './js/api-client.js';
import * as adminAuth from './js/auth-session.js';
import * as adminNotifications from './js/notifications.js';
import {
  TICKET_STATUS_LABELS, TICKET_PRIORITY_LABELS, TICKET_ASSIGNEE_LABELS,
  SUPERVISOR_ROLE_DISPLAY, SUPERVISOR_ROLE_NAMES,
  ESCALATION_SUPERVISOR_TARGETS,
  BILLING_COMPLAINT_TYPES, SALES_COMPLAINT_TYPES,
  ASSIGNABLE_ROLE_NAMES, CUSTOMER_CARE_ASSIGN_ROLES,
  CUSTOMER_CARE_SUPERVISOR_ROLE, FIELD_TECH_ROLE, FIELD_TECH_SUPERVISOR_ROLE,
  SYSTEM_ADMIN_ROLE,
  TICKET_PRIORITY_OPTIONS, TICKET_COMPLAINT_OPTIONS,
  ENQUIRY_TYPE_OPTIONS, CONTACT_METHOD_LABELS, CONTACT_METHOD_OPTIONS,
  CHANNEL_PREFERRED_CONTACT, TICKET_VISIBILITY_LABELS,
  COMMENT_REVIEW_LABELS, COMMENT_EDITABLE_STATES, COMMENT_RETRY_STATES,
  COMMENT_OVERRIDE_STATES, COMMENT_SUPERVISOR_ROLES,
  TICKET_CLOSE_TEMPLATES, META_ORIGIN_ALIASES,
  ASSIGN_MODE_ASSIGN, ASSIGN_MODE_ESCALATE,
  formatTicketStatus, formatTicketComplaint, formatTicketPriority, formatTicketAssignee,
  formatLaneCollapsedLabel, describeDuration, formatAiRecommendations,
  resolveTicketDisplay,
  extractRoleNames, userIsSupervisor, derivePrimaryAssignmentRole,
  describeSupervisorTargets, deriveSupervisorAdminRoles,
  userEligibleForTicketAssignment,
  fallbackAdminContact, deriveAdminDisplayName, primaryAdminUserName,
  adminUserSortKey, contactIsRedundant,
  formatAdminUserLabel, formatAssignmentOptionLabel,
  setCurrentUserGetter as setTicketsCurrentUserGetter,
  setTechnicianProfileLookup,
} from './src/lib/ticket-utils.js';


    // The full URL map and the auth-aware fetch live in
    // `frontend/admin/js/api-client.js` (loaded by admin.html before this
    // file). We re-bind to short locals so the existing call sites read
    // unchanged. Phase 1.1 of the admin-frontend decomposition plan.
    const API = adminAPI.routes;

    const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24h
    // Theme constants + read/write helpers live in `src/lib/theme.js`
    // (Phase 3.1.a.4). The boot call to `ensureThemeInitialized()` below
    // is what wires the initial `<html data-theme>` attribute.
    let ADMIN_ME = { roles: [] };
    // Wire `lib/roles.js` to read our live `ADMIN_ME` reference. The thunk
    // is re-evaluated on every `hasRole(...)` call so role refreshes after
    // login propagate without explicit notification.
    setRolesCurrentUserGetter(() => ADMIN_ME);
    setTicketsCurrentUserGetter(() => ADMIN_ME);

    // Wire `lib/auth.js` (Phase 3.1.b). All thunks are lazy — they read
    // values that are declared later in this file. Function declarations
    // (applyRoleGating, switchView, openDialog, populateTicketAssigneeUserFilter,
    // applyDefaultAssigneeUserFilter, ensureTechnicianUsersLoaded,
    // ticketAssigneeUserManual_get) are hoisted into module scope; the
    // const refs (viewElements, analyticsIframe) are read inside the
    // thunks so they only resolve at call time, after their declarations
    // have executed.
    configureAuth({
      getAdminMe: () => ADMIN_ME,
      setAdminMe: (value) => { ADMIN_ME = value; },
      applyRoleGating: () => applyRoleGating(),
      switchView: (name, el) => switchView(name, el),
      getViewElements: () => viewElements,
      getAnalyticsIframe: () => analyticsIframe,
      populateTicketAssigneeUserFilter: (selected) => populateTicketAssigneeUserFilter(selected),
      applyDefaultAssigneeUserFilter: () => applyDefaultAssigneeUserFilter(),
      ensureTechnicianUsersLoaded: (opts) => ensureTechnicianUsersLoaded(opts),
      ticketAssigneeUserManualGet: () => ticketAssigneeUserManual_get(),
      openDialog: (el) => openDialog(el),
      sessionTtlMs: SESSION_TTL_MS,
      apiRoutes: adminAPI.routes,
    });

    // ===== Phase 2 view registry =====
    // Per-page-section view modules under `frontend/admin/js/views/` register
    // themselves here as `window.adminViews[clusterName] = { … }`. The shape
    // is intentionally loose for now — each cluster decides how to expose
    // sub-views (e.g. `adminViews.tickets.regions = { mount, refresh, … }`).
    // admin.js only needs the namespace to exist so view modules that load
    // before this script can `(window.adminViews ||= {})` without races.
    window.adminViews = window.adminViews || {};
    const adminViews = window.adminViews;

    // The notification WebSocket lifecycle (open/reconnect/close) and the
    // toast/flash helpers live in `frontend/admin/js/notifications.js`.
    // `notificationIdentity` is admin-domain (set on connection.ack inside
    // `handleNotificationMessage`), so it stays here.
    let notificationIdentity = null;
    // Aliases keep the existing 550+ `showFlash(...)` and 7
    // `pushNotificationToast(...)` call sites compiling without churn —
    // the canonical names live on `window.adminNotifications`.
    const showFlash = adminNotifications.showFlash;
    const pushNotificationToast = adminNotifications.pushToast;
    const ensureNotificationSocket = adminNotifications.ensureSocket;
    const closeNotificationSocket = adminNotifications.closeSocket;
    const ensureToastLayer = adminNotifications.ensureToastLayer;
    // `ticketCommentCache`, `ticketCommentPendingRefresh`,
    // `autoStatusUpdateInFlight`, `ticketCommentEditState` shims are
    // declared below (Phase 2.1.d.5).
    // `ticketAttentionSet` shim is declared below (Phase 2.1.d.4).
    // `ticketBillingState`, `ticketBillingTicketId`, `ticketDetailCurrent`,
    // `ticketActivityState`, `ticketTimelineExpanded`, `ticketListCollapsed`,
    // `slaPoliciesState`, and the detail-render functions
    // (`renderTicketDetail`, `renderTicketCustomer`, `renderTicketDescription`,
    // `renderTicketMeta`, `renderTicketSlaPolicy`, `renderTicketBilling`,
    // `renderTicketTimeline`, `loadTicketBilling`, `loadTicketActivities`,
    // `setTicketBadge`, `setTicketListCollapsed`, `setTicketMetaVisibility`,
    // `getRawPortaCustomerId`, `resolvePortaCustomerId`,
    // `hasPlaceholderPortaCustomer`, `extractLatestCustomerSentiment`,
    // `buildLinkedTicketsMarkup`, `formatSlaPolicySummary`,
    // `populateSlaPolicySelect`, `ensureSlaPoliciesLoaded`, `computeSla`,
    // `getTicketCustomerName`) all live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.3). Reach via
    // `adminViews.tickets.detail.*`.
    const __detail = () => (window.adminViews && window.adminViews.tickets && window.adminViews.tickets.detail);
    const renderTicketDetail = (ticket) => { const d = __detail(); if (d) d.render(ticket); };
    const renderTicketCustomer = (ticket) => { const d = __detail(); if (d) d.renderCustomer(ticket); };
    const renderTicketDescription = (ticket) => { const d = __detail(); if (d) d.renderDescription(ticket); };
    const renderTicketMeta = (ticket) => { const d = __detail(); if (d) d.renderMeta(ticket); };
    const renderTicketSlaPolicy = (ticket) => { const d = __detail(); if (d) d.renderSlaPolicy(ticket); };
    const renderTicketTimeline = (ticket) => { const d = __detail(); if (d) d.renderTimeline(ticket); };
    const renderTicketBilling = (data) => { const d = __detail(); if (d) d.renderBilling(data); };
    const resetTicketBillingCard = () => { const d = __detail(); if (d) d.resetBillingCard(); };
    const loadTicketBilling = (id, opts) => { const d = __detail(); return d ? d.loadTicketBilling(id, opts) : Promise.resolve(); };
    const loadTicketActivities = (id) => { const d = __detail(); if (d) d.loadTicketActivities(id); };
    const setTicketBadge = (el, kind, value, label) => { const d = __detail(); if (d) d.setBadge(el, kind, value, label); };
    const setTicketListCollapsed = (collapsed, opts) => { const d = __detail(); if (d) d.setListCollapsed(collapsed, opts); };
    const setTicketMetaVisibility = (show) => { const d = __detail(); if (d) d.setMetaVisibility(show); };
    const getRawPortaCustomerId = (ticket) => { const d = __detail(); return d ? d.getRawPortaCustomerId(ticket) : ''; };
    const resolvePortaCustomerId = (ticket) => { const d = __detail(); return d ? d.resolvePortaCustomerId(ticket) : ''; };
    const hasPlaceholderPortaCustomer = (ticket) => { const d = __detail(); return d ? d.hasPlaceholderPortaCustomer(ticket) : false; };
    const extractLatestCustomerSentiment = (ticket) => { const d = __detail(); return d ? d.extractLatestCustomerSentiment(ticket) : null; };
    const buildLinkedTicketsMarkup = (linkedTickets) => { const d = __detail(); return d ? d.buildLinkedTicketsMarkup(linkedTickets) : ''; };
    const populateSlaPolicySelect = (selectedId) => { const d = __detail(); if (d) d.populateSlaPolicySelect(selectedId); };
    const formatSlaPolicySummary = (policy) => { const d = __detail(); return d ? d.formatSlaPolicySummary(policy) : ''; };
    const ensureSlaPoliciesLoaded = (opts) => { const d = __detail(); return d ? d.ensureSlaPoliciesLoaded(opts) : Promise.resolve([]); };
    const computeSla = (ticket) => { const d = __detail(); return d ? d.computeSla(ticket) : { label: '', breached: false, className: '' }; };
    const getTicketCustomerName = (ticket) => { const d = __detail(); return d ? d.getTicketCustomerName(ticket) : null; };
    const ticketBillingState = new Proxy({}, {
      get(_, prop) { const d = __detail(); return d ? d.getBillingState()[prop] : undefined; },
      set(_, prop, value) {
        const d = __detail(); if (!d) return true;
        const next = { ...d.getBillingState(), [prop]: value };
        d.setBillingState(next);
        return true;
      },
    });
    function ticketBillingTicketId_get() { const d = __detail(); return d ? d.getBillingTicketId() : null; }
    function ticketBillingTicketId_set(v) { const d = __detail(); if (d) d.setBillingTicketId(v); }
    function ticketDetailCurrent_get() { const d = __detail(); return d ? d.getCurrent() : null; }
    function ticketDetailCurrent_set(v) { const d = __detail(); if (d) d.setCurrent(v); }
    function ticketListCollapsed_get() { const d = __detail(); return d ? d.isListCollapsed() : false; }

    // ---- Phase 2.1.d.4 thin shims --------------------------------------
    // Inline editors, customer-edit/identify/duplicate/conflict/assign
    // dialogs, ticket-action menu (`handleTicketAction`/`mutateTicket`),
    // SLA-policy change handler, and attention helpers all live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.4). Reach via
    // `adminViews.tickets.detailInteractions.*`.
    const __int = () => (window.adminViews && window.adminViews.tickets && window.adminViews.tickets.detailInteractions);
    const resetTicketSubjectEditor = (value) => { const m = __int(); if (m) m.resetSubjectEditor(value); };
    const openTicketSubjectEditor = (value) => { const m = __int(); if (m) m.openSubjectEditor(value); };
    const submitTicketSubject = () => { const m = __int(); return m ? m.submitSubject() : Promise.resolve(); };
    const resetTicketComplaintEditor = (value) => { const m = __int(); if (m) m.resetComplaintEditor(value); };
    const openTicketComplaintEditor = (value) => { const m = __int(); if (m) m.openComplaintEditor(value); };
    const submitTicketComplaint = () => { const m = __int(); return m ? m.submitComplaint() : Promise.resolve(); };
    const resetTicketCustomerNameEditor = (value, canEdit) => { const m = __int(); if (m) m.resetCustomerNameEditor(value, canEdit); };
    const openTicketCustomerNameEditor = (value) => { const m = __int(); if (m) m.openCustomerNameEditor(value); };
    const submitTicketCustomerName = () => { const m = __int(); return m ? m.submitCustomerName() : Promise.resolve(); };
    const openTicketCustomerEditDialog = () => { const m = __int(); return m ? m.openCustomerEditDialog() : Promise.resolve(); };
    const closeTicketCustomerEditDialog = () => { const m = __int(); if (m) m.closeCustomerEditDialog(); };
    const openIdentifyCustomerDialog = (ticket) => { const m = __int(); return m ? m.openIdentifyDialog(ticket) : Promise.resolve(); };
    const closeIdentifyCustomerDialog = () => { const m = __int(); if (m) m.closeIdentifyDialog(); };
    const openTicketDuplicateDialog = (ticket) => { const m = __int(); return m ? m.openDuplicateDialog(ticket) : Promise.resolve(); };
    const closeTicketDuplicateDialog = () => { const m = __int(); if (m) m.closeDuplicateDialog(); };
    const submitTicketDuplicateSelection = () => { const m = __int(); return m ? m.submitDuplicateSelection() : Promise.resolve(); };
    const openTicketConflictDialog = (options) => { const m = __int(); if (m) m.openConflictDialog(options); };
    const closeTicketConflictDialog = () => { const m = __int(); if (m) m.closeConflictDialog(); };
    const handleTicketConflictChange = (event) => { const m = __int(); if (m) m.handleConflictChange(event); };
    const handleTicketConflictClick = (event) => { const m = __int(); if (m) m.handleConflictClick(event); };
    const openTicketAssignDialog = (ticket, options) => { const m = __int(); return m ? m.openAssignDialog(ticket, options) : Promise.resolve(); };
    const closeTicketAssignDialog = () => { const m = __int(); if (m) m.closeAssignDialog(); };
    const resetTicketAssignDialog = () => { const m = __int(); if (m) m.resetAssignDialog(); };
    const handleTicketAction = (action) => { const m = __int(); return m ? m.handleAction(action) : Promise.resolve(); };
    const mutateTicket = (ticketId, payload) => { const m = __int(); return m ? m.mutateTicket(ticketId, payload) : Promise.reject(new Error('detailInteractions not loaded')); };
    const handleTicketSlaPolicyChange = (event) => { const m = __int(); return m ? m.handleSlaPolicyChange(event) : Promise.resolve(); };
    const markTicketAttention = (ticketId, options) => { const m = __int(); if (m) m.markAttention(ticketId, options); };
    const clearTicketAttention = (ticketId) => { const m = __int(); if (m) m.clearAttention(ticketId); };
    const applyTicketAttentionHighlight = (targetId) => { const m = __int(); if (m) m.applyAttentionHighlight(targetId); };
    const updateNavTicketsHighlight = () => { const m = __int(); if (m) m.updateNavHighlight(); };
    const ticketAttentionSet = new Proxy({}, {
      get(_, prop) {
        const m = __int(); if (!m) return undefined;
        const set = m.getAttentionSet();
        const v = set[prop];
        return typeof v === 'function' ? v.bind(set) : v;
      },
    });
    const ticketDuplicateState = new Proxy({}, {
      get(_, prop) { const m = __int(); return m ? m.getDuplicateState()[prop] : undefined; },
      set(_, prop, value) { const m = __int(); if (m) m.getDuplicateState()[prop] = value; return true; },
    });
    const ticketConflictState = new Proxy({}, {
      get(_, prop) { const m = __int(); return m ? m.getConflictState()[prop] : undefined; },
      set(_, prop, value) { const m = __int(); if (m) m.getConflictState()[prop] = value; return true; },
    });

    // ---- Phase 2.1.d.5 thin shims --------------------------------------
    // Comment list/edit/submit, attachments, expand dialog, sanitization,
    // and Quill instance management all live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.5). Reach via
    // `adminViews.tickets.detailComments.*`.
    const __comm = () => (window.adminViews && window.adminViews.tickets && window.adminViews.tickets.detailComments);
    const COMMENT_ATTACHMENT_LIMIT = 6;
    const COMMENT_ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;
    const renderTicketComments = (comments) => { const m = __comm(); if (m) m.renderComments(comments); };
    const renderTicketCommentsForTicket = (id) => { const m = __comm(); if (m) m.renderForTicket(id); };
    const updateTicketCommentsStatus = (count, error) => { const m = __comm(); if (m) m.updateStatus(count, error); };
    const buildTicketCommentAttachmentsMarkup = (attachments) => { const m = __comm(); return m ? m.buildAttachmentsMarkup(attachments) : ''; };
    const buildCommentFeedback = (comment) => { const m = __comm(); return m ? m.buildFeedback(comment) : ''; };
    const loadTicketComments = (id) => { const m = __comm(); return m ? m.loadComments(id) : Promise.resolve(); };
    const submitTicketComment = (opts) => { const m = __comm(); return m ? m.submitComment(opts) : Promise.resolve(); };
    const startTicketCommentEdit = (id) => { const m = __comm(); if (m) m.startEdit(id); };
    const cancelTicketCommentEdit = () => { const m = __comm(); if (m) m.cancelEdit(); };
    const saveTicketCommentEdit = (id, btn) => { const m = __comm(); return m ? m.saveEdit(id, btn) : Promise.resolve(); };
    const retryTicketComment = (id, btn) => { const m = __comm(); return m ? m.retryComment(id, btn) : Promise.resolve(); };
    const overrideTicketComment = (id, btn) => { const m = __comm(); return m ? m.overrideComment(id, btn) : Promise.resolve(); };
    const applyCommentSuggestion = (id, btn) => { const m = __comm(); if (m) m.applySuggestion(id, btn); };
    const maybeAutoUpdateStatusFromComments = (id, comments) => { const m = __comm(); return m ? m.maybeAutoUpdateStatus(id, comments) : Promise.resolve(); };
    const findCommentInCache = (ticketId, commentId) => { const m = __comm(); return m ? m.findCommentInCache(ticketId, commentId) : undefined; };
    const isCustomerFeedbackComment = (comment) => { const m = __comm(); return m ? m.isCustomerFeedbackComment(comment) : false; };
    const canEditComment = (comment) => { const m = __comm(); return m ? m.canEditComment(comment) : false; };
    const canRetryComment = (comment) => { const m = __comm(); return m ? m.canRetryComment(comment) : false; };
    const canOverrideComment = (comment) => { const m = __comm(); return m ? m.canOverrideComment(comment) : false; };
    const handleTicketCommentClick = (event) => { const m = __comm(); if (m) m.handleClick(event); };
    const handleTicketCommentInput = (event) => { const m = __comm(); if (m) m.handleInput(event); };
    const handleTicketCommentVisibilityChange = (eventOrValue) => { const m = __comm(); if (m) m.handleVisibilityChange(eventOrValue); };
    const handleTicketCommentExpandVisibilityChange = (eventOrValue) => { const m = __comm(); if (m) m.handleExpandVisibilityChange(eventOrValue); };
    const ensureTicketCommentExpandEditor = () => { const m = __comm(); return m ? m.ensureExpandEditor() : null; };
    const refreshTicketCommentExpandThread = () => { const m = __comm(); if (m) m.refreshExpandThread(); };
    const openTicketCommentExpandDialog = () => { const m = __comm(); if (m) m.openExpandDialog(); };
    const closeTicketCommentExpandDialog = (options) => { const m = __comm(); if (m) m.closeExpandDialog(options); };
    const openTicketAttachmentPicker = (options) => { const m = __comm(); if (m) m.openAttachmentPicker(options); };
    const addTicketCommentAttachments = (files) => { const m = __comm(); return m ? m.addAttachments(files) : Promise.resolve(); };
    const replaceTicketCommentAttachment = (id, file) => { const m = __comm(); return m ? m.replaceAttachment(id, file) : Promise.resolve(); };
    const removeTicketCommentAttachment = (id) => { const m = __comm(); if (m) m.removeAttachment(id); };
    const renderTicketCommentAttachments = () => { const m = __comm(); if (m) m.renderAttachments(); };
    const resetTicketCommentAttachments = () => { const m = __comm(); if (m) m.resetAttachments(); };
    const handleTicketAttachmentListClick = (event) => { const m = __comm(); if (m) m.handleAttachmentListClick(event); };
    const handleTicketAttachmentInputChange = (event) => { const m = __comm(); return m ? m.handleAttachmentInputChange(event) : Promise.resolve(); };
    const sanitizeTicketCommentHtml = (html) => { const m = __comm(); return m ? m.sanitizeCommentHtml(html) : ''; };
    const commentHtmlToPlain = (html) => { const m = __comm(); return m ? m.commentHtmlToPlain(html) : ''; };
    const plainTextToCommentHtml = (text) => { const m = __comm(); return m ? m.plainTextToCommentHtml(text) : ''; };
    function ticketCommentQuill_get() { const m = __comm(); return m ? m.getQuill() : null; }
    function ticketCommentExpandQuill_get() { const m = __comm(); return m ? m.getExpandQuill() : null; }
    const ticketCommentCache = new Proxy({}, {
      get(_, prop) {
        const m = __comm(); if (!m) return undefined;
        const cache = m.getCommentCache();
        const v = cache[prop];
        return typeof v === 'function' ? v.bind(cache) : v;
      },
    });
    const ticketCommentPendingRefresh = new Proxy({}, {
      get(_, prop) {
        const m = __comm(); if (!m) return undefined;
        const set = m.getCommentPendingRefresh();
        const v = set[prop];
        return typeof v === 'function' ? v.bind(set) : v;
      },
    });
    const autoStatusUpdateInFlight = new Proxy({}, {
      get(_, prop) {
        const m = __comm(); if (!m) return undefined;
        const set = m.getAutoStatusUpdateInFlight();
        const v = set[prop];
        return typeof v === 'function' ? v.bind(set) : v;
      },
    });
    const ticketCommentEditState = new Proxy({}, {
      get(_, prop) { const m = __comm(); return m ? m.getEditState()[prop] : undefined; },
      set(_, prop, value) {
        const m = __comm(); if (!m) return true;
        const next = { ...m.getEditState(), [prop]: value };
        m.setEditState(next);
        return true;
      },
    });
    const ticketCommentAttachmentsState = new Proxy({}, {
      get(_, prop) { const m = __comm(); return m ? m.getAttachmentsState()[prop] : undefined; },
      set(_, prop, value) { const m = __comm(); if (m) m.getAttachmentsState()[prop] = value; return true; },
    });

    // `COMMENT_ATTACHMENT_LIMIT`, `COMMENT_ATTACHMENT_MAX_BYTES`, and
    // `ticketCommentAttachmentsState` live in `views.tickets.detailComments`
    // (Phase 2.1.d.5). Reach via the shim below.
    // `ticketDuplicateState` and `ticketConflictState` shims are declared
    // above (Phase 2.1.d.4).
    // ticketCreatePrefill moved into tickets.js (Phase 3.2.c).
    // `ticketDetailCurrent` lives in `views.tickets.detail`. The `__detail()`
    // shim above exposes it via getter/setter functions; direct reads have
    // been routed to `ticketDetailCurrent_get()`.
    // Shift management DOM refs moved into shift-management.js (Phase 2.3.k.2).
    // Shift state (shiftPromptDismissed/OptionsCache/AvailableTrunks/CloseState)
    // moved into shift-management.js (Phase 2.3.k.2).
    // shiftLinesInput readOnly setup moved into shift-management.js (Phase 2.3.k.2).
    // HR + shift-template state (`shiftTemplatesState`, `shiftActiveState`,
    // `hrActiveTab`, `hrSection`, `hrPeopleTab`, `hrSectionPanels`,
    // `hrDataState`, `hrDepartmentsList`, `hrPositionsList`, `hrPeopleList`,
    // `hrLoansList`, `hrEmployeeSectionState`, etc.) lives in
    // `frontend/admin/js/views/hr.js` (Phase 2.2.d).
    // adminUsersCache + getRegisteredPbxTrunks moved into
    // `frontend/admin/js/views/shift-management.js` (Phase 3.2.c) — those
    // were only ever read by the shift-management view.
    // Shift state + banner + alert + polling were already moved
    // (Phase 2.3.k.2).

    // readThemeCookie / getThemePreference / setThemePreference /
    // updateThemeToggle / applyTheme / ensureThemeInitialized live in
    // `src/lib/theme.js` (Phase 3.1.a.4). The boot call below paints the
    // initial `<html data-theme>` attribute so the rest of the script
    // renders against the user's preferred theme.
    ensureThemeInitialized();

    // CHANNEL_OPTIONS / CHANNEL_LABEL_MAP / getChannelLabel /
    // PBX_EVENT_OPTIONS / DEFAULT_PBX_EVENTS live in `src/lib/channels.js`
    // (Phase 3.2.c). AI_PROMPT_LABELS moved into ai-prompts.js (Phase 2.3.j.1).
    // NEW_MAILBOX_OPTION + channelSettingsInitialized + channelSettingsState
    // + channelStatusElements + channelControlSync moved into
    // `frontend/admin/js/views/channel-settings.js` (Phase 2.3.j.4).
    // Shift role/group constants moved into shift-management.js (Phase 2.3.k.2).

    // isValidTimeString + normalizePbxConfig live in `src/lib/pbx.js`
    // (Phase 3.2.c).
    // aiPromptHistoryState + aiPromptEditors moved into ai-prompts.js (Phase 2.3.j.1).
    // templateFilters + templateFilterTimer + templatePreviewState moved into message-templates.js (Phase 2.3.j.2).

    // ===== UTIL: token storage =====
    // The session lifecycle (setSessionExpiry / getToken / isSessionValid /
    // clearToken) lives in `frontend/admin/js/auth-session.js` (loaded by
    // admin.html before this file). We hand it the SPA-side hooks below and
    // then call into it via `adminAuth.*` at the use sites. The token itself
    // rides in an HttpOnly cookie set on /auth/login — JavaScript can't read
    // it, which mitigates token theft via XSS.
    adminAuth.configure({
      defaultTtlMs: SESSION_TTL_MS,
      logoutUrl: adminAPI.routes.logout || '/auth/logout',
      // Both transitions refresh the badge in the top bar.
      onChange: () => { try { updateSessionStatus(); } catch {} },
      // Session-start side effects: bring up the WS push channel.
      onStart: () => { try { ensureNotificationSocket(); } catch {} },
      // Session-end side effects: drop ticking timers and close the WS.
      onEnd: () => {
        const sm = window.adminViews && window.adminViews.shiftManagement;
        try { if (sm) sm.stopPolling(); } catch {}
        try { if (sm) sm.clearEndAlert({ resetSchedule: true }); } catch {}
        try { closeNotificationSocket(true); } catch {}
      },
    });

    // Wire the API client (loaded by admin.html before this file). The 401
    // handler delegates to `adminAuth.clearToken()` so logout cleanup stays
    // in one place. `formatCrmLabel` is a function declaration hoisted from
    // later in this file, so it's already in scope here.
    adminAPI.configure({
      formatLabel: (value) => formatCrmLabel(value),
      onUnauthorized: () => {
        try { adminAuth.clearToken(); } catch {}
        const overlay = document.getElementById('loginOverlay');
        if (overlay) { overlay.hidden = false; overlay.style.display = 'grid'; }
      },
    });

    // Phase 2.1.b: hand the pending-messages view module the admin.js
    // helpers it needs. All referenced symbols are function declarations
    // hoisted into scope by the JS engine, so we can close over them here
    // even though they're defined later in the file.
    //
    // After 2.1.b.3-detail moved the detail/conversion/dedup/enquiry/discard
    // code into the module, `pendingMessageCurrent` and the various
    // populate/open/fetchRecording functions are now internal — admin.js
    // only needs to inject the genuinely cross-cutting helpers below.

    // tickets.list view imports helpers directly (Phase 3.2.c). The
    // `ticketCreatePrefill` Map moved into tickets.js (only that cluster
    // reads it).

    // ES-module hoisting trap: this file is imported by `src/main.js` at the
    // bottom of that file, but ALL static imports are hoisted to the top, so
    // admin.js's body executes BEFORE main.js's `window.adminViews.X = X`
    // assignments. At that moment `adminViews.X` is undefined and every
    // `if (adminViews.X) X.mount()` below silently short-circuits — mount()
    // never runs and the orphan top-level listeners (search inputs, tab
    // bars, refresh buttons) never bind. `queueMicrotask` defers the whole
    // boot-mount block to a microtask, which fires AFTER main.js's
    // top-level body finishes and `window.adminViews.X` is populated.
    queueMicrotask(() => {
    // CRM customers + prospects (Phase 2.2.c) — view imports helpers directly (Phase 3.2.c).
    if (adminViews.crm) {
      adminViews.crm.mount();
    }

    // crmInstallations view imports helpers directly (Phase 3.2.c).
    if (adminViews.crmInstallations) {
      adminViews.crmInstallations.mount();
    }

    // CRM config / Porta plans (Phase 2.2.c.3) — inject helpers. No mount
    // step: the view router calls `init()` on first open, which wires
    // crmConfig view imports helpers directly (Phase 3.2.c).

    // HR (Phase 2.2.d) — view imports helpers directly (Phase 3.2.c).
    // The mount() call wires the orphan top-level listeners that
    // originally fired at admin.js script-load time. The view router
    // calls `init()` when the user opens the HR tab.
    if (adminViews.hr) {
      adminViews.hr.mount();
    }

    // Logs (Phase 2.3.a) — inject helpers + mount.
    // logs view imports its helpers directly from src/lib (Phase 3.2.c).
    if (adminViews.logs) {
      adminViews.logs.mount();
    }

    // Customers (Phase 2.3.b) — inject helpers + mount.
    // customers view imports helpers directly (Phase 3.2.c).
    if (adminViews.customers) {
      adminViews.customers.mount();
    }

    // users view imports helpers directly (Phase 3.2.c).
    if (adminViews.users) {
      adminViews.users.mount();
    }

    // products view imports helpers directly (Phase 3.2.c).
    if (adminViews.products) {
      adminViews.products.mount();
    }

    // Technicians (Phase 2.3.i) — inject helpers. mount() not used; the
    // module's `init()` is the entry point (called by the view router).
    // technicians view imports helpers directly (Phase 3.2.c).

    // shiftManagement view imports helpers directly + owns adminUsersCache
    // (Phase 3.2.c). It reads ADMIN_ME via getCurrentAdminUser from
    // src/lib/roles.js.
    if (adminViews.shiftManagement) {
      adminViews.shiftManagement.mount();
    }

    // dailyStatus view imports helpers directly (Phase 3.2.c).
    if (adminViews.dailyStatus) {
      adminViews.dailyStatus.attachListeners();
    }

    // Duplicate Messages (Phase 2.3.j.5) — inject helpers + wire listeners.
    // duplicateMessages view imports helpers directly (Phase 3.2.c).
    if (adminViews.duplicateMessages) {
      adminViews.duplicateMessages.attachListeners();
    }

    // Email outbox + Tools-tab switcher (Phase 2.3.j.6).
    // emailOutbox view imports helpers directly (Phase 3.2.c).
    if (adminViews.emailOutbox) {
      adminViews.emailOutbox.attachListeners();
    }

    // channelSettings view imports helpers directly (Phase 3.2.c). It owns
    // `channelSettingsState` shared with the AI Prompts / Message Templates /
    // WhatsApp CSAT sub-views.

    // whatsappCsat reads channelSettings state lazily via window.adminViews
    // (Phase 3.2.c).

    // Message Templates (Phase 2.3.j.2) — inject helpers + state getter.
    // Listeners are wired by `attachListeners` from inside
    // `initChannelSettingsView`.
    // messageTemplates view imports helpers directly (Phase 3.2.c).
    if (adminViews.messageTemplates) {
      adminViews.messageTemplates.configureChannelSettingsState(() => adminViews.channelSettings.getState());
    }

    // AI prompts (Phase 2.3.j.1) — view imports helpers directly (Phase 3.2.c).
    // The prompts state lives inside admin.js's `channelSettingsState` shared
    // with templates + WhatsApp CSAT. Pass a getter so the module reads/writes
    // it directly. Listeners are wired by `attachListeners` from inside
    // `initChannelSettingsView`.
    if (adminViews.aiPrompts) {
      adminViews.aiPrompts.configurePromptsState(() => adminViews.channelSettings.getState());
    }

    // Sessions + diagnostics (Phase 2.3.h) — inject helpers + mount.
    // sessions view imports helpers directly (Phase 3.2.c).
    if (adminViews.sessions) {
      adminViews.sessions.mount();
    }

    // Campaigns (Phase 2.3.f) — inject helpers + mount.
    // `switchView` is a function declaration so it's fully hoisted and
    // safe to reference here; `viewTitle` is a const declared further
    // down, so reach for it via DOM lookup instead.
    // campaigns view imports helpers directly + listens for `admin:viewchange`
    // to update its topbar button (Phase 3.2.c).
    if (adminViews.campaigns) {
      adminViews.campaigns.mount();
    }

    // accessPoints view imports helpers directly (Phase 3.2.c).
    if (adminViews.accessPoints) {
      adminViews.accessPoints.mount();
    }

    // mobileCustomers view imports helpers directly (Phase 3.2.c).
    if (adminViews.mobileCustomers) {
      adminViews.mobileCustomers.mount();
    }

    // distributors view imports helpers directly (Phase 3.2.c).
    if (adminViews.distributors) {
      adminViews.distributors.mount();
    }

    // techReview view imports helpers directly (Phase 3.2.c).
    if (adminViews.tickets && adminViews.tickets.techReview) {
      adminViews.tickets.techReview.mount();
    }

    // tickets.detailInteractions view imports helpers directly (Phase 3.2.c).
    if (adminViews.tickets && adminViews.tickets.detailInteractions) {
      adminViews.tickets.detailInteractions.mount();
    }

    // tickets.detail view imports helpers directly (Phase 3.2.c).

    // Create ticket dialog (Phase 2.1.d.2b) — inject helpers.
    // tickets.create view imports helpers directly (Phase 3.2.c).

    // Technician (main) ticket board — view imports helpers directly (Phase 3.2.c).
    // The board renders inside both the Tickets view ("board" mode) and via
    // cross-flow toggles in the technicians cluster, so we wire mount()
    // unconditionally rather than tying it to a single view loader.
    if (adminViews.tickets && adminViews.tickets.technicianBoard) {
      adminViews.tickets.technicianBoard.mount();
    }
    });  // end queueMicrotask: deferred boot-mount block (see start of block)

    // Customer-care supervisor board (Phase 2.1.c) — view imports helpers
    // directly + uses local references for cross-board state (Phase 3.2.c).

    // ===== UTIL: notification dedup state =====
    // Toast and flash rendering live in `frontend/admin/js/notifications.js`
    // (aliased above as `showFlash` / `pushNotificationToast`). The dedup map
    // below stays here because it's keyed by admin-domain notion (per ticket
    // id, per pending message id, per pbx call) and only `handleNotificationMessage`
    // and the queue helpers below use it.

    // `queuePendingMessageNotification` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).
    // ===== UTIL: API fetch + error formatting =====
    // The full implementation lives in `frontend/admin/js/api-client.js`.
    // Call sites in this file use `adminAPI.fetch(...)` directly. The 401
    // handler is wired via `adminAPI.configure({ onUnauthorized })` higher
    // up (right after the adminAuth.configure block), where it delegates to
    // `adminAuth.clearToken()` and shows the login overlay.

    // Notification WebSocket lifecycle — open/reconnect/close — lives in
    // `frontend/admin/js/notifications.js`. We hand it the auth gate
    // (`adminAuth.isSessionValid`) and the message dispatcher
    // (`handleNotificationMessage`) below. The aliases at the top of this
    // file (`ensureNotificationSocket`, `closeNotificationSocket`) keep the
    // existing call sites in `adminAuth.configure`'s onStart/onEnd hooks
    // working unchanged.
    adminNotifications.configure({
      isAuthorized: () => adminAuth.isSessionValid(),
      onMessage: (event) => handleNotificationMessage(event),
    });

    function handleNotificationMessage(event) {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!payload || typeof payload !== 'object') return;
      const type = payload.type;
      if (Array.isArray(payload.audience_roles) && payload.audience_roles.length) {
        const audienceAllowed = payload.audience_roles.some((role) => hasRole(role));
        if (!audienceAllowed) {
          return;
        }
      }
      if (Array.isArray(payload.audience_user_ids) && payload.audience_user_ids.length) {
        const adminId = ADMIN_ME && typeof ADMIN_ME.id !== 'undefined' ? Number(ADMIN_ME.id) : null;
        const allowed = adminId != null && payload.audience_user_ids.some((id) => Number(id) === adminId);
        if (!allowed) {
          return;
        }
      }
      if (type === 'connection.ack') {
        notificationIdentity = payload.data || null;
        return;
      }
      if (type === 'report.job.completed') {
        const data = payload.data || {};
        const reportKey = data.report_key || 'report';
        const downloadUrl = data.download_url || '';
        const rows = data.row_count != null ? ` (${formatNumber(data.row_count)} rows)` : '';
        pushNotificationToast({
          title: 'Report export ready',
          message: `${reportKey.replace(/_/g, ' ')} export is ready${rows}.`,
          icon: '📈',
          kind: 'success',
          autoCloseMs: 15000,
          onClick: () => {
            if (downloadUrl) window.open(downloadUrl, '_blank', 'noopener');
          },
        });
        return;
      }
      if (type === 'report.job.failed') {
        const data = payload.data || {};
        const reportKey = data.report_key || 'report';
        const reason = data.error || 'Export failed';
        pushNotificationToast({
          title: 'Report export failed',
          message: `${reportKey.replace(/_/g, ' ')} export failed. ${reason}`,
          icon: '⚠️',
          kind: 'error',
          autoCloseMs: 15000,
        });
        return;
      }
      if (type === 'shift.end_warning') {
        const sm = window.adminViews && window.adminViews.shiftManagement;
        if (sm) {
          sm.setPromptDismissed(false);
          sm.clearEndAlert({ resetSchedule: false });
          sm.refreshStatus({ silent: true })
            .then((info) => {
              sm.evaluateEndAlert(info || sm.getCurrentShift());
            })
            .catch(() => {
              sm.evaluateEndAlert(sm.getCurrentShift());
            });
        }
        return;
      }
      if (type === 'ticket.board_delta') {
        handleTicketBoardDelta(payload.data || {});
        return;
      }
      if (type === 'crm.prospect.changed') {
        const crm = window.adminViews && window.adminViews.crm;
        if (crm && typeof crm.onProspectChanged === 'function') {
          crm.onProspectChanged(payload.data || {});
        }
        return;
      }
      if (type === 'pbx.event.dead_letter') {
        if (!hasRole('system_admin', 'root')) return;
        const data = payload.data || {};
        const messageId = data.message_id ? String(data.message_id) : `pbx-${Date.now()}`;
        const key = `pbx-dead-letter:${messageId}`;
        const now = Date.now();
        const recentAt = notificationRecent.get(key) || 0;
        if (now - recentAt < 15000) {
          return;
        }
        notificationRecent.set(key, now);
        const topic = String(data.topic || 'unknown');
        const callId = data.payload && typeof data.payload.call_id === 'string' ? data.payload.call_id : null;
        const title = 'PBX event requires attention';
        const msgParts = [`Unroutable PBX event (${topic})`];
        if (callId) msgParts.push(`Call ID ${callId}`);
        const message = `${msgParts.join(' — ')}. Review PBX channel settings.`;
        pushNotificationToast({
          id: `${key}-${now}`,
          title,
          message,
          icon: '☎️',
          kind: 'error',
          autoCloseMs: 0,
          onClick: () => {
            switchView('channel_settings');
            const pbxDetails = document.querySelector('details[data-channel="pbx"]');
            if (pbxDetails) {
              pbxDetails.open = true;
              pbxDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          },
          data: { messageId },
        });
        return;
      }
      if (type === 'ticket_comment.reviewed') {
        const data = payload.data || {};
        if (!data.ticket_id) return;
        const ticketId = String(data.ticket_id);
        ticketCommentCache.delete(ticketId);
        const selected = getSelectedTicket();
        if (selected && String(selected.id) === ticketId) {
          if (ticketCommentEditState.commentId) {
            ticketCommentPendingRefresh.add(ticketId);
            return;
          }
          loadTicketComments(selected.id);
        } else {
          ticketCommentPendingRefresh.add(ticketId);
          markTicketAttention(ticketId, { silent: true });
        }
        return;
      }
      if (type === 'conversation.message') {
        const data = payload.data || {};
        if (!data.ticket_id) return;
        const ticketId = String(data.ticket_id);
        const authorType = (data.author_type || '').toLowerCase();
        const payloadSubject = typeof data.subject === 'string' ? data.subject : '';
        const payloadShortCode = data.ticket_short_code ? String(data.ticket_short_code) : '';
        ticketCommentCache.delete(ticketId);
        const selected = getSelectedTicket();
        if (selected && String(selected.id) === ticketId) {
          if (ticketCommentEditState.commentId) {
            ticketCommentPendingRefresh.add(ticketId);
            markTicketAttention(ticketId, { silent: true, subject: payloadSubject, shortCode: payloadShortCode });
          } else {
            clearTicketAttention(ticketId);
            loadTicketComments(selected.id);
          }
        } else {
          ticketCommentPendingRefresh.add(ticketId);
          if (authorType === 'customer') {
            markTicketAttention(ticketId, { subject: payloadSubject, shortCode: payloadShortCode });
          }
          if (!findTicketById(ticketId)) {
            reloadTickets({ keepSelection: true }).catch(() => {});
          }
        }
        return;
      }
      if (type === 'ticket.assigned') {
        if (!canAccessTicketing()) return;
        const data = payload.data || {};
        const ticketId = data.ticket_id ? String(data.ticket_id) : null;
        if (!ticketId) return;
        const subject = typeof data.subject === 'string' ? data.subject : '';
        const shortCode = data.ticket_short_code ? String(data.ticket_short_code) : '';
        queueTicketNotification({
          ticketId,
          kind: 'assignment',
          subject,
          shortCode,
        });
        if (ticketsInitialized_get()) {
          reloadTickets({ keepSelection: true }).catch(() => {});
        } else {
          initTicketsView();
        }
        refreshUnassignedSnapshot().catch(() => {});
        const tb1 = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
        if (tb1) tb1.markChanged();
        return;
      }
      if (type === 'ticket.tech_closure_submitted') {
        if (!canAccessTicketing()) return;
        const data = payload.data || {};
        const tb2 = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
        if (tb2) tb2.markChanged();
        const ds = window.adminViews && window.adminViews.dailyStatus;
        if (ds) {
          ds.markStale();
          if (ds.isOpen()) ds.load({ silent: true }).catch(() => {});
        }
        const shortCode = data.short_code ? `#${data.short_code}` : (data.ticket_id ? `#${String(data.ticket_id).slice(0, 8)}` : 'ticket');
        const technicianName = data.technician_display_name || 'Technician';
        showFlash(`${technicianName} submitted closure for ${shortCode}`, 'info', 4000);
        return;
      }
      if (type === 'ticket.tech_closure_quality_alert') {
        if (!canAccessTicketing()) return;
        const data = payload.data || {};
        const tb3 = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
        if (tb3) tb3.markChanged();
        const ds = window.adminViews && window.adminViews.dailyStatus;
        if (ds) {
          ds.markStale();
          if (ds.isOpen()) ds.load({ silent: true }).catch(() => {});
        }
        const shortCode = data.short_code ? `#${data.short_code}` : (data.ticket_id ? `#${String(data.ticket_id).slice(0, 8)}` : 'ticket');
        let reason = '';
        const flags = Array.isArray(data.flags) ? data.flags.filter(Boolean) : [];
        if (flags.length) {
          reason = `Flags: ${flags.join(', ')}`;
        } else if (typeof data.score === 'number' && typeof data.threshold === 'number') {
          reason = `Score ${Math.round(data.score)} < ${Math.round(data.threshold)}`;
        } else {
          reason = 'Low quality evidence';
        }
        showFlash(`AI alert ${shortCode} — ${reason}`, 'error', 7000);
        return;
      }
      if (type === 'ticket.created') {
        if (!canAccessTicketing()) return;
        const data = payload.data || {};
        const ticketId = data.ticket_id ? String(data.ticket_id) : null;
        const creatorId = data.created_by != null ? Number(data.created_by) : null;
        reloadTickets({ keepSelection: true }).catch(() => {});
        refreshUnassignedSnapshot().catch(() => {});
        const tb4 = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
        if (tb4) tb4.markChanged();
        if (!creatorId || !ADMIN_ME || Number(ADMIN_ME.id || 0) !== creatorId) {
          const subject = typeof data.subject === 'string' && data.subject.trim() ? data.subject.trim() : ticketId || 'ticket';
          showFlash(`New ticket created: ${subject}`);
        }
        return;
      }
      if (type === 'pbx.call.incoming') {
        if (!adminViews.tickets.pendingMessages.list.canAccess()) return;
        const data = payload.data || {};
        const pendingIdRaw = data.pending_message_id;
        if (pendingIdRaw == null) return;
        const pendingId = String(pendingIdRaw);
        const trunkRaw = typeof data.trunk === 'string' ? data.trunk.trim() : '';
        const trunkKey = trunkRaw.toLowerCase();
        const registeredTrunks = getRegisteredPbxTrunks();
        if (registeredTrunks.length && trunkKey && !registeredTrunks.includes(trunkKey)) {
          return;
        }
        const pendingKey = `pbx:auto-open:${pendingId}`;
        const loadAndOpen = () => {
          adminViews.tickets.pendingMessages.list.fetchDetail(pendingId)
            .then((record) => {
              if (!record) return;
              adminViews.tickets.pendingMessages.detail.open(pendingId, { skipFocus: false });
            })
            .catch((err) => {
              const message = err && err.message ? err.message : 'Unable to open PBX intake dialog.';
              showFlash(message, 'error');
            });
        };
        const now = Date.now();
        const recentAt = notificationRecent.get(pendingKey) || 0;
        if (now - recentAt > 1500) {
          notificationRecent.set(pendingKey, now);
          const lineLabel = trunkRaw || 'PBX line';
          pushNotificationToast({
            id: `${pendingKey}-${now}`,
            title: 'Incoming PBX call',
            message: `${lineLabel}: finish what you’re doing, then click to open the call dialog.`,
            icon: '☎️',
            kind: 'info',
            autoCloseMs: 0,
            onClick: () => {
              loadAndOpen();
            },
            data: { pendingMessageId: pendingId, callId: data.call_id || null },
          });
        }
        adminViews.tickets.pendingMessages.list.setIndicator(true);
        adminViews.tickets.pendingMessages.list.setStale(true);
        return;
      }
      if (type === 'pending_message.recorded') {
        if (!adminViews.tickets.pendingMessages.list.canAccess()) return;
        const data = payload.data || {};
        const messageId = data.message_id;
        const isActive = adminViews.tickets.pendingMessages.list.isViewActive();
        if (isActive) {
          if (!adminViews.tickets.pendingMessages.list.isLoaded() || !messageId) {
            adminViews.tickets.pendingMessages.list.refresh({ keepSelection: true }).catch(() => {});
          } else {
            adminViews.tickets.pendingMessages.list.fetchDetail(messageId).catch(() => {});
          }
          adminViews.tickets.pendingMessages.list.setIndicator(false);
        } else {
          adminViews.tickets.pendingMessages.list.setStale(true);
        }
        const channel = typeof data.channel === 'string' ? data.channel.toUpperCase() : 'PENDING';
        if (!isActive) {
          adminViews.tickets.pendingMessages.detail.queueIncomingNotification({
            messageId,
            channel,
            subject: data.subject,
            customerContact: data.customer_contact,
          });
          if (adminViews.tickets.pendingMessages.list.normalizeStatus(data.status) === 'pending') {
            adminViews.tickets.pendingMessages.list.setIndicator(true);
          }
        }
        return;
      }
      if (type === 'pending_message.updated') {
        if (!adminViews.tickets.pendingMessages.list.canAccess()) return;
        const data = payload.data || {};
        const messageId = data.message_id;
        const isActive = adminViews.tickets.pendingMessages.list.isViewActive();
        if (isActive) {
          if (!adminViews.tickets.pendingMessages.list.isLoaded() || !messageId) {
            adminViews.tickets.pendingMessages.list.refresh({ keepSelection: true }).catch(() => {});
          } else {
            adminViews.tickets.pendingMessages.list.fetchDetail(messageId).catch(() => {});
          }
          adminViews.tickets.pendingMessages.list.setIndicator(false);
        } else {
          adminViews.tickets.pendingMessages.list.setStale(true);
        }
        if (data.action === 'sla_warning') {
          const label = data.subject || data.customer_contact || data.message_id || 'pending message';
          showFlash(`Pending message SLA warning: ${label}`, 'error', 7000);
        }
        if (!isActive && adminViews.tickets.pendingMessages.list.normalizeStatus(data.status) === 'pending') {
          adminViews.tickets.pendingMessages.list.setIndicator(true);
        }
        return;
      }
      if (type === 'ticket.duplicate_linked') {
        if (!canAccessTicketing()) return;
        const data = payload.data || {};
        const primaryId = data.primary_ticket_id ? String(data.primary_ticket_id) : null;
        const primaryLabel = (data.primary_short_code && `#${data.primary_short_code}`)
          || (data.primary_subject && data.primary_subject.trim())
          || primaryId
          || 'ticket';
        const duplicateLabel = (data.duplicate_short_code && `#${data.duplicate_short_code}`)
          || (data.duplicate_subject && data.duplicate_subject.trim())
          || (data.duplicate_ticket_id ? String(data.duplicate_ticket_id) : 'duplicate');
        const isPrimaryAssignee = data.primary_assignee_user_id != null
          && ADMIN_ME
          && typeof ADMIN_ME.id !== 'undefined'
          && Number(data.primary_assignee_user_id) === Number(ADMIN_ME.id);
        if (isPrimaryAssignee) {
          showFlash(`Linked duplicate ${duplicateLabel} to ${primaryLabel}.`);
        }
        if (primaryId) {
          reloadTickets({ keepSelection: true, selectId: primaryId }).catch(() => {});
        }
        const tb5 = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
        if (tb5) tb5.markChanged();
        return;
      }
    }

    // ===== Session gate =====
    // gate / closeProfileMenu / updateProfileMenuLabel / openProfileDialog
    // live in `src/lib/auth.js` (Phase 3.1.b). The init() IIFE below calls
    // the imported `gate()`. Lookup of `loginOverlay` / `loginForm` /
    // `profileDialog` / etc. is owned by `src/lib/auth.js`.

    // Shift role helpers (Phase 2.3.k.2) moved into shift-management.js.

    // `canAccessPendingMessages` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.4). Reach via
    // `adminViews.tickets.pendingMessages.list.canAccess()`.



    function getDefaultAssignedLinesForCurrentUser() {
      const roles = Array.isArray(ADMIN_ME?.roles) ? ADMIN_ME.roles.map((role) => String(role)) : [];
      if (!roles.length) return [];
      const collected = [];
      const seen = new Set();
      roles.forEach((role) => {
        const key = role.toLowerCase();
        const defaults = SHIFT_DEFAULT_LINE_BY_ROLE[key];
        if (!defaults) return;
        defaults.forEach((line) => {
          const value = String(line || '').trim();
          if (!value) return;
          const dedupeKey = value.toLowerCase();
          if (seen.has(dedupeKey)) return;
          seen.add(dedupeKey);
          collected.push(value);
        });
      });
      return collected;
    }

    // Shift dialogs + handlers (Phase 2.3.k.2) moved into
    // `frontend/admin/js/views/shift-management.js`.

	    function applyRoleGating() {
	      // Gate nav entries
	      const analyticsNav = document.querySelector('.nav .nav-view[data-view="analytics"]');
	      const usersNav = document.querySelector('.nav .nav-view[data-view="users"]');
	      const campaignsNav = document.querySelector('.nav .nav-view[data-view="campaigns"]');
	      const apNav = document.querySelector('.nav .nav-view[data-view="access_points"]');
	      const hotspotTestNav = document.querySelector('.nav .nav-view[data-view="hotspot_test"]');
	      const customersNav = document.querySelector('.nav .nav-view[data-view="customers"]');
	      const productsNav = document.querySelector('.nav .nav-view[data-view="products"]');
	      const mobileSessionsNav = document.querySelector('.nav .nav-view[data-view="mobilesessions"]');
	      const mobileCustomersNav = document.querySelector('.nav .nav-view[data-view="mobile_customers"]');
      const logsNav = document.querySelector('.nav .nav-view[data-view="logs"]');
      const ticketsNav = document.querySelector('.nav .nav-view[data-view="tickets"]');
      const ticketsUnassignedNav = document.querySelector('.nav .nav-view[data-ticket-quick="unassigned"]');
      const pendingNav = document.querySelector('.nav .nav-view[data-view="pending_messages"]');
      const customerBoardNav = document.querySelector('.nav .nav-view[data-view="customer_board"]');
      const techniciansNav = document.querySelector('.nav .nav-view[data-view="technicians"]');
      const hrNavs = document.querySelectorAll('.nav .nav-view[data-view="hr"]');
      const channelSettingsNav = document.querySelector('.nav .nav-view[data-view="channel_settings"]');
      const crmCustomersNav = document.querySelector('.nav .nav-view[data-view="crm_customers"]');
      const crmProspectsNav = document.querySelector('.nav .nav-view[data-view="crm_prospects"]');
      const crmInstallationsNav = document.querySelector('.nav .nav-view[data-view="crm_installations"]');
      const crmConfigNav = document.querySelector('.nav .nav-view[data-view="crm_config"]');
      const reportsNav = document.querySelector('.nav .nav-view[data-view="reports"]');
      const toolsNav = document.querySelector('.nav .nav-view[data-view="tools"]');
      const securityNav = document.querySelector('.nav .nav-view[data-view="security"]');
      const canAccessHr = hasRole('root','hr_admin','hr_manager');
      if (analyticsNav) analyticsNav.style.display = hasRole('root','customer_care_supervisor') ? '' : 'none';
	      if (usersNav) usersNav.style.display = hasRole('root','system_admin') ? '' : 'none';
      // Security owns account lockouts and blocked addresses — same two roles
      // that manage users, and the ones MFA is mandatory for.
      if (securityNav) securityNav.style.display = hasRole('root','system_admin') ? '' : 'none';
	      if (campaignsNav) campaignsNav.style.display = hasRole('root','hotspot_admin') ? '' : 'none';
	      if (apNav) apNav.style.display = hasRole('root','hotspot_admin') ? '' : 'none';
	      if (hotspotTestNav) hotspotTestNav.style.display = hasRole('root','hotspot_admin') ? '' : 'none';
	      if (customersNav) customersNav.style.display = hasRole('root','hotspot_admin') ? '' : 'none';
	      if (productsNav) productsNav.style.display = hasRole('root','hotspot_admin') ? '' : 'none';
	      if (mobileSessionsNav) mobileSessionsNav.style.display = hasRole('root','customer_care','customer_care_supervisor','system_admin') ? '' : 'none';
	      if (mobileCustomersNav) mobileCustomersNav.style.display = hasRole('root','customer_care','customer_care_supervisor') ? '' : 'none';
      if (logsNav) logsNav.style.display = hasRole('root','system_admin','customer_care','customer_care_supervisor') ? '' : 'none';
      // Call-center agents/supervisors get full access to CRM customers and
      // prospects (they generate and work them), alongside sales and root.
      const crmAllowed = hasRole('root','sales_officer','sales_supervisor','customer_care','customer_care_supervisor');
      if (crmCustomersNav) crmCustomersNav.style.display = crmAllowed ? '' : 'none';
      if (crmProspectsNav) crmProspectsNav.style.display = crmAllowed ? '' : 'none';
      const crmInstallAllowed = hasRole('root','sales_officer','sales_supervisor','billing_officer','billing_supervisor','inventory_supervisor','installation_admin','field_tech_supervisor','customer_care_supervisor','customer_care');
      if (crmInstallationsNav) crmInstallationsNav.style.display = crmInstallAllowed ? '' : 'none';
      if (reportsNav) reportsNav.style.display = crmInstallAllowed ? '' : 'none';
      // CRM "Tools" submenu. Targets stays root / customer care / sales
      // supervisor; Reconciliation additionally includes the customer care
      // supervisor. The subgroup shows if the user can see anything inside it,
      // and each item is gated individually. Backend endpoints enforce the same.
      const crmToolsGroup = document.querySelector('.nav .nav-group[data-group="crm_tools"]');
      const crmToolsAllowed = hasRole('root','customer_care','sales_supervisor');
      const crmReconciliationAllowed = crmToolsAllowed || hasRole('customer_care_supervisor');
      if (crmToolsGroup) crmToolsGroup.style.display = crmReconciliationAllowed ? '' : 'none';
      const crmTargetsNav = document.querySelector('.nav .nav-view[data-view="crm_targets"]');
      if (crmTargetsNav) crmTargetsNav.style.display = crmToolsAllowed ? '' : 'none';
      // Quote approvals — root + sales supervisor (matches the inbox endpoint gate).
      // Approvals — one nav item with Quotes + Invoices/receipts tabs. Shown if the
      // user can see either; the combined badge sums both pending counts.
      const quoteApprovalsAllowed = hasRole('root', 'sales_supervisor');
      const invoicingReviewAllowed = hasRole('root', 'finance_manager', 'sales_supervisor', 'billing_supervisor');
      const invoicingReviewsNav = document.querySelector('.nav .nav-view[data-view="invoicing_reviews"]');
      const invoicingReviewsAllowed = quoteApprovalsAllowed || invoicingReviewAllowed;
      if (invoicingReviewsNav) {
        invoicingReviewsNav.style.display = invoicingReviewsAllowed ? '' : 'none';
        if (invoicingReviewsAllowed) {
          const rv = window.adminViews && window.adminViews.invoicingReviews;
          if (rv && typeof rv.refreshBadge === 'function') rv.refreshBadge();
        }
      }
      const invoicingCalendarNav = document.querySelector('.nav .nav-view[data-view="invoicing_calendar"]');
      const invoicingCalendarAllowed = hasRole('root', 'sales_officer', 'sales_supervisor', 'billing_officer', 'billing_supervisor', 'customer_care_supervisor');
      if (invoicingCalendarNav) {
        invoicingCalendarNav.style.display = invoicingCalendarAllowed ? '' : 'none';
        if (invoicingCalendarAllowed) {
          const cv = window.adminViews && window.adminViews.invoicingCalendar;
          if (cv && typeof cv.refreshBadge === 'function') cv.refreshBadge();
        }
      }
      const invoicingTaxationNav = document.querySelector('.nav .nav-view[data-view="invoicing_taxation"]');
      const invoicingTaxationAllowed = hasRole('root', 'finance_manager', 'sales_officer', 'sales_supervisor', 'billing_officer', 'billing_supervisor');
      if (invoicingTaxationNav) invoicingTaxationNav.style.display = invoicingTaxationAllowed ? '' : 'none';
      // The CRM → Invoicing subgroup (Reminders / Reviews / Quote approvals / Taxation)
      // shows if the user can see any item inside it; each item is gated individually above.
      const crmInvoicingGroup = document.querySelector('.nav .nav-group[data-group="crm_invoicing"]');
      if (crmInvoicingGroup) {
        crmInvoicingGroup.style.display =
          (invoicingCalendarAllowed || invoicingReviewAllowed || quoteApprovalsAllowed || invoicingTaxationAllowed) ? '' : 'none';
      }
      const crmReconciliationNav = document.querySelector('.nav .nav-view[data-view="crm_reconciliation"]');
      if (crmReconciliationNav) crmReconciliationNav.style.display = crmReconciliationAllowed ? '' : 'none';
      // Retention worklist: customer care + sales and their supervisors.
      const crmRetentionNav = document.querySelector('.nav .nav-view[data-view="crm_retention"]');
      if (crmRetentionNav) crmRetentionNav.style.display = hasRole('root','customer_care','customer_care_supervisor','sales_officer','sales_supervisor') ? '' : 'none';
      if (crmConfigNav) crmConfigNav.style.display = hasRole('root','system_admin','sales_supervisor') ? '' : 'none';
      const systemHealthNav = document.querySelector('.nav .nav-view[data-view="system_health"]');
      if (systemHealthNav) systemHealthNav.style.display = hasRole('root','system_admin') ? '' : 'none';
      // Inventory — only the dedicated inventory-console roles (root + stock/finance).
      // Infra tech/supervisor are deliberately excluded: they draw materials via the
      // Operations → work-order flow, not the inventory console.
      const canAccessInventory = hasRole('root','storekeeper','inventory_supervisor','inventory_finance','support_tech');
      ['navInventory','navInventoryLedger','navInventoryAssets','navInventoryPurchasing'].forEach((id) => {
        const n = document.getElementById(id); if (n) n.style.display = canAccessInventory ? '' : 'none';
      });

      // Operations (Infra-Ops). An infra_tech works ONLY with work orders; a
      // supervisor/manager (and root) see the full console (Team / Facilities /
      // Maintenance). The whole group hides for anyone with no operations role.
      const opsWorkOrders = hasRole('root','infra_tech','infra_tech_supervisor','infra_manager');
      const opsFull = hasRole('root','infra_tech_supervisor','infra_manager');
      const navInfraOps = document.getElementById('navInfraOps');
      if (navInfraOps) navInfraOps.style.display = opsWorkOrders ? '' : 'none';
      ['navInfraTeam','navInfraFacilities','navInfraCalendar'].forEach((id) => {
        const n = document.getElementById(id); if (n) n.style.display = opsFull ? '' : 'none';
      });
      // NOC Outages — root / noc_admin (matches the outage endpoints).
      const nocNav = document.querySelector('.nav .nav-view[data-view="noc_outages"]');
      if (nocNav) nocNav.style.display = hasRole('root','noc_admin') ? '' : 'none';
      hrNavs.forEach((nav) => { nav.style.display = canAccessHr ? '' : 'none'; });
      // "My HR" self-service portal: shown to ANY user whose account is linked
      // to an employee record (no role gate) — the backend also enforces this.
      const essPortalNav = document.querySelector('.nav .nav-view[data-view="ess_portal"]');
      if (essPortalNav) essPortalNav.style.display = (ADMIN_ME && ADMIN_ME.employee_id) ? '' : 'none';
      // Flash a badge on "My HR" when the user has leave approvals to action.
      const essBadge = document.getElementById('essApprovalsNavBadge');
      if (essBadge) {
        const n = (ADMIN_ME && Number(ADMIN_ME.pending_approvals)) || 0;
        essBadge.textContent = n > 0 ? String(n) : '';
        essBadge.style.display = n > 0 ? '' : 'none';
      }
      if (pendingMessageEnquiryBtn) {
        const enquiryAllowed = adminViews.tickets.pendingMessages.list.canAccess();
        pendingMessageEnquiryBtn.style.display = enquiryAllowed ? '' : 'none';
      }
      const canAccessTickets = canAccessTicketing();
      if (ticketsNav) ticketsNav.style.display = canAccessTickets ? '' : 'none';
      if (ticketsUnassignedNav) ticketsUnassignedNav.style.display = canAccessTickets ? '' : 'none';
      if (pendingNav) pendingNav.style.display = canAccessTickets ? '' : 'none';
      if (customerBoardNav) customerBoardNav.style.display = hasCustomerBoardAccess() ? '' : 'none';
      const canManageTechnicians = hasRole('root','system_admin','field_tech_supervisor');
      if (techniciansNav) techniciansNav.style.display = canManageTechnicians ? '' : 'none';
      if (channelSettingsNav) channelSettingsNav.style.display = hasRole('root','system_admin') ? '' : 'none';
      if (toolsNav) toolsNav.style.display = hasRole('root', 'billing_supervisor', 'installation_admin') ? '' : 'none';
      const sm_uc = window.adminViews && window.adminViews.shiftManagement;
      if (sm_uc) sm_uc.updateControls();
      // Gate create buttons and write actions in current views
      if (addTicketBtn) {
        const allowed = canCreateTickets();
        addTicketBtn.dataset.allowed = allowed ? '1' : '0';
        addTicketBtn.style.display = allowed ? '' : 'none';
      }
      if (dailyStatusBtn) {
        const allowedDaily = hasRole('field_tech_supervisor', 'customer_care', 'customer_care_supervisor', 'root');
        dailyStatusBtn.dataset.allowed = allowedDaily ? '1' : '0';
        if (!allowedDaily) dailyStatusBtn.style.display = 'none';
      }
      if (addTechnicianBtn) {
        addTechnicianBtn.dataset.allowed = canManageTechnicians ? '1' : '0';
        addTechnicianBtn.style.display = canManageTechnicians ? '' : 'none';
      }
      addProductBtn.style.display = hasRole('root','hotspot_admin') ? '' : 'none';
      scanApButton.style.display = hasRole('root','hotspot_admin') ? '' : 'none';
      updateGroupVisibility();

      const activeNav = document.querySelector('.nav .nav-view.active');
      const visibleNavs = Array.from(document.querySelectorAll('.nav .nav-view')).filter(el => el.style.display !== 'none');
      if (!activeNav || activeNav.style.display === 'none') {
        const fallback = visibleNavs.find(el => el.dataset.view);
        if (fallback) {
          switchView(fallback.dataset.view, fallback);
        }
      }
    }

    function updateGroupVisibility() {
      document.querySelectorAll('.nav-group').forEach(group => {
        const visibleChild = Array.from(group.querySelectorAll('.nav-view')).some(item => item.style.display !== 'none');
        group.style.display = visibleChild ? '' : 'none';
      });
    }
    updateGroupVisibility();

    // ===== Sidebar / views =====
    const appEl = document.getElementById('app');
    const contentRoot = document.querySelector('.content');
    if (contentRoot) {
      const activeNav = document.querySelector('.nav .nav-view.active');
      if (activeNav && activeNav.dataset.view === 'analytics') {
        contentRoot.classList.add('analytics-active');
      }
    }
    document.getElementById('collapseBtn').addEventListener('click', () => appEl.classList.toggle('collapsed'));
    const viewTitle = document.getElementById('viewTitle');
	    const analyticsView = document.getElementById('analyticsView');
	    const analyticsIframe = analyticsView ? analyticsView.querySelector('iframe') : null;
	    const productsView = document.getElementById('productsView');
	    const accessPointsView = document.getElementById('accessPointsView');
	    const hotspotTestView = document.getElementById('hotspotTestView');
	    const mobileSessionsView = document.getElementById('mobileSessionsView');
	    const scanApButton = document.getElementById('scanApButton');
	    const apTabList = document.getElementById('apTabList');
	    const apTabSessions = document.getElementById('apTabSessions');
	    const apTabBtnList = document.getElementById('apTabBtnList');
	    const apTabBtnSessions = document.getElementById('apTabBtnSessions');
	    const hotspotTestStartBtn = document.getElementById('hotspotTestStartBtn');
	    const hotspotTestNewMacBtn = document.getElementById('hotspotTestNewMacBtn');
	    const hotspotTestReloadBtn = document.getElementById('hotspotTestReloadBtn');
	    const hotspotTestFrame = document.getElementById('hotspotTestFrame');
	    const hotspotTestStatus = document.getElementById('hotspotTestStatus');
    const hotspotTestMac = document.getElementById('hotspotTestMac');
    const hotspotTestGateway = document.getElementById('hotspotTestGateway');
    const hotspotTestFlow = document.getElementById('hotspotTestFlow');
    const hotspotTestAndroidVer = document.getElementById('hotspotTestAndroidVer');
	    const usersView = document.getElementById('usersView');
	    const customersView = document.getElementById('customersView');
	    const customersTabBtnCustomers = document.getElementById('customersTabBtnCustomers');
	    const customersTabBtnDistributors = document.getElementById('customersTabBtnDistributors');
	    const customersTabCustomersWrap = document.getElementById('customersTabCustomersWrap');
	    const customersTabDistributorsWrap = document.getElementById('customersTabDistributorsWrap');
	    const distributorSearch = document.getElementById('distributorSearch');
	    const addDistributorBtn = document.getElementById('addDistributorBtn');
	    const distributorsTbody = document.getElementById('distributorsTbody');
	    const distributorDlg = document.getElementById('distributorDlg');
	    const distributorForm = document.getElementById('distributorForm');
	    const distributorDlgTitle = document.getElementById('distributorDlgTitle');
	    const distributorDetailDlg = document.getElementById('distributorDetailDlg');
	    const distributorDetailTitle = document.getElementById('distributorDetailTitle');
	    const distributorDetailSummary = document.getElementById('distributorDetailSummary');
	    const distributorManualTopupBtn = document.getElementById('distributorManualTopupBtn');
	    const distributorAps = document.getElementById('distributorAps');
	    const distributorSales = document.getElementById('distributorSales');
	    const distributorWallet = document.getElementById('distributorWallet');
	    const distributorTopups = document.getElementById('distributorTopups');
	    const distributorPayouts = document.getElementById('distributorPayouts');
	    const campaignsView = document.getElementById('campaignsView');
    const logsView = document.getElementById('logsView');
    const addTicketBtn = document.getElementById('addTicketBtn');
    const shiftSetupBtn = document.getElementById('shiftSetupBtn');
    const dailyStatusBtn = document.getElementById('dailyStatusBtn');
    const addProductBtn = document.getElementById('addProductBtn');
    const addUserBtn = document.getElementById('addUserBtn');
    const customersToolbar = document.getElementById('customersToolbar');
    const mobileCustomersView = document.getElementById('mobileCustomersView');
    const themeToggle = document.getElementById('themeToggle');
    const ticketsView = document.getElementById('ticketsView');
    const shiftBanner = document.getElementById('shiftBanner');
    const navTicketsAll = document.getElementById('navTicketsAll');
    const navTicketsUnassigned = document.getElementById('navTicketsUnassigned');
    // `navPendingMessages` is resolved inside
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.4).
    const navCrmCustomers = document.getElementById('navCrmCustomers');
    const navCrmProspects = document.getElementById('navCrmProspects');
    // `customerBoardView` is still resolved here so the `viewElements` map
    // (the show/hide table consumed by `switchView`) can flip its `hidden`
    // attribute. The rest of the customer-board DOM lives inside
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.c) and is reached
    // through `adminViews.tickets.customerBoard`.
    const customerBoardView = document.getElementById('customerBoardView');
    const techniciansView = document.getElementById('techniciansView');
    const techResolutionTrend = document.getElementById('techResolutionTrend');
    const techResolutionTrendNote = document.getElementById('techResolutionTrendNote');
    const techResolutionStrip = techResolutionTrend ? techResolutionTrend.closest('.tech-resolution-strip') : null;
    const techResolutionLegend = document.getElementById('techResolutionLegend');
    const hrView = document.getElementById('hrView');
    const toolsView = document.getElementById('toolsView');
    // HR + shift-template DOM refs (filters, dialogs, tables, panels,
    // and ~440 element bindings) moved into
    // `frontend/admin/js/views/hr.js` (Phase 2.2.d) and resolved lazily
    // inside that module's `resolveDom()`.
    const techniciansTableWrap = document.getElementById('techniciansTableWrap');
    const techModeBoardBtn = document.getElementById('techModeBoardBtn');
    const techModeTableBtn = document.getElementById('techModeTableBtn');
    const ticketsTbody = document.getElementById('ticketsTbody');
    const ticketsPrev = document.getElementById('ticketsPrev');
    const ticketsNext = document.getElementById('ticketsNext');
    const ticketsPageInfo = document.getElementById('ticketsPageInfo');
    const ticketsCount = document.getElementById('ticketsCount');
    const ticketSearchInput = document.getElementById('ticketSearch');
    const ticketStatusFilter = document.getElementById('ticketStatusFilter');
    const ticketClosedFilter = document.getElementById('ticketClosedFilter');
    const ticketPriorityFilter = document.getElementById('ticketPriorityFilter');
    const ticketAssigneeFilter = document.getElementById('ticketAssigneeFilter');
    const ticketAssigneeUserFilter = document.getElementById('ticketAssigneeUserFilter');
    const ticketOutageFilter = document.getElementById('ticketOutageFilter');
    const ticketSlaFilter = document.getElementById('ticketSlaFilter');
    const ticketTypeFilter = document.getElementById('ticketTypeFilter');
    const ticketAgeFilter = document.getElementById('ticketAgeFilter');
    const ticketSortFilter = document.getElementById('ticketSortFilter');
    const ticketResetFilters = document.getElementById('ticketResetFilters');
    const ticketDetailBody = document.getElementById('ticketDetailBody');
    const crmCustomersView = document.getElementById('crmCustomersView');
    const crmReconciliationView = document.getElementById('crmReconciliationView');
    const crmRetentionView = document.getElementById('crmRetentionView');
    const essPortalPanel = document.getElementById('essPortalView');
    const crmCustomerSearchInput = document.getElementById('crmCustomerSearch');
    const crmCustomersRefreshBtn = document.getElementById('crmCustomersRefresh');
    const crmCustomersTbody = document.getElementById('crmCustomersTbody');
    const crmCustomersPageInfo = document.getElementById('crmCustomersPageInfo');
    const crmCustomersPrevBtn = document.getElementById('crmCustomersPrev');
    const crmCustomersNextBtn = document.getElementById('crmCustomersNext');

	    const crmProspectsView = document.getElementById('crmProspectsView');
	    const crmTargetsView = document.getElementById('crmTargetsView');
	    const crmQuoteApprovalsView = document.getElementById('crmQuoteApprovalsView');
	    const invoicingReviewView = document.getElementById('invoicingReviewView');
	    const invoicingReviewsView = document.getElementById('invoicingReviewsView');
	    const invoicingCalendarView = document.getElementById('invoicingCalendarView');
	    const invoicingTaxationView = document.getElementById('invoicingTaxationView');

	    let accessPointsActiveTab = 'list';
	    function setAccessPointsTab(tab) {
	      if (!apTabList || !apTabSessions || !apTabBtnList || !apTabBtnSessions) return;
	      const next = tab === 'sessions' ? 'sessions' : 'list';
	      accessPointsActiveTab = next;
	      apTabList.style.display = next === 'list' ? '' : 'none';
	      apTabSessions.style.display = next === 'sessions' ? '' : 'none';
	      apTabBtnList.className = `btn${next === 'list' ? ' primary' : ''}`;
	      apTabBtnSessions.className = `btn${next === 'sessions' ? ' primary' : ''}`;
	      if (scanApButton) scanApButton.style.display = next === 'list' ? '' : 'none';
	      if (next === 'list') {
	        const m = window.adminViews && window.adminViews.accessPoints;
	        if (m) m.init();
	      } else {
	        const s = window.adminViews && window.adminViews.sessions;
	        if (s) {
	          s.loadAuthmonStats();
	          s.loadAPSessions();
	          s.loadGraceSessions();
	        }
	      }
	    }
	    if (apTabBtnList) apTabBtnList.addEventListener('click', () => setAccessPointsTab('list'));
	    if (apTabBtnSessions) apTabBtnSessions.addEventListener('click', () => setAccessPointsTab('sessions'));
    const crmProspectSearchInput = document.getElementById('crmProspectSearch');
    const crmProspectsRefreshBtn = document.getElementById('crmProspectsRefresh');
    const crmProspectsNewBtn = document.getElementById('crmProspectsNewBtn');
    const crmProspectsTbody = document.getElementById('crmProspectsTbody');
    const crmProspectsPageInfo = document.getElementById('crmProspectsPageInfo');
    const crmProspectsPrevBtn = document.getElementById('crmProspectsPrev');
    const crmProspectsNextBtn = document.getElementById('crmProspectsNext');
    const crmProspectDialog = document.getElementById('crmProspectDlg');
    const crmProspectForm = document.getElementById('crmProspectForm');
    const crmProspectSubmitBtn = document.getElementById('crmProspectSubmit');
    const crmProspectNameInput = document.getElementById('crmProspectNameInput');
    const crmProspectPhoneInput = document.getElementById('crmProspectPhoneInput');
    const crmProspectEmailInput = document.getElementById('crmProspectEmailInput');
    const crmProspectCityInput = document.getElementById('crmProspectCityInput');
    const crmProspectAddressInput = document.getElementById('crmProspectAddressInput');
    const crmProspectCompanyInput = document.getElementById('crmProspectCompanyInput');
    const crmProspectNotesInput = document.getElementById('crmProspectNotesInput');
    const crmInstallationsView = document.getElementById('crmInstallationsView');
    const inventoryView = document.getElementById('inventoryView');
    const inventoryLedgerView = document.getElementById('inventoryLedgerView');
    const inventoryAssetsView = document.getElementById('inventoryAssetsView');
    const inventoryPurchasingView = document.getElementById('inventoryPurchasingView');
    const infraOpsView = document.getElementById('infraOpsView');
    const infraTeamView = document.getElementById('infraTeamView');
    const infraFacilitiesView = document.getElementById('infraFacilitiesView');
    const infraCalendarView = document.getElementById('infraCalendarView');
    const nocOutagesView = document.getElementById('nocOutagesView');
    const reportsView = document.getElementById('reportsView');
    const securityView = document.getElementById('securityView');
    const systemHealthView = document.getElementById('systemHealthView');
    // CRM installation DOM refs (filters, table, dialogs, dashboards, etc.)
    // moved into `frontend/admin/js/views/crm-installations.js` (Phase
    // 2.2.c.2). They are resolved lazily inside that module's `resolveDom()`.
    const crmConfigView = document.getElementById('crmConfigView');
    // CRM config DOM refs (filters, table, dialog, form fields) moved into
    // `frontend/admin/js/views/crm-config.js` (Phase 2.2.c.3) and resolved
    // lazily inside that module's `resolveDom()`.
    const ticketDetailEmpty = document.getElementById('ticketDetailEmpty');
    const ticketDetailSubject = document.getElementById('ticketDetailSubject');
    const ticketSubjectEditBtn = document.getElementById('ticketSubjectEditBtn');
    const ticketSubjectEditor = document.getElementById('ticketSubjectEditor');
    const ticketSubjectInlineInput = document.getElementById('ticketSubjectInput');
    const ticketSubjectSaveBtn = document.getElementById('ticketSubjectSaveBtn');
    const ticketSubjectCancelBtn = document.getElementById('ticketSubjectCancelBtn');
    const ticketDetailComplaint = document.getElementById('ticketDetailComplaint');
    const ticketComplaintEditBtn = document.getElementById('ticketComplaintEditBtn');
    const ticketComplaintEditor = document.getElementById('ticketComplaintEditor');
    const ticketComplaintInput = document.getElementById('ticketComplaintInput');
    const ticketComplaintSaveBtn = document.getElementById('ticketComplaintSaveBtn');
    const ticketComplaintCancelBtn = document.getElementById('ticketComplaintCancelBtn');
    const ticketDetailStatus = document.getElementById('ticketDetailStatus');
    const ticketDetailPriority = document.getElementById('ticketDetailPriority');
    // Profile menu / dialog DOM refs + logout button now resolved inside
    // `src/lib/auth.js` (Phase 3.1.b).
    const ticketDetailAssignee = document.getElementById('ticketDetailAssignee');
    const ticketDetailEscalated = document.getElementById('ticketDetailEscalated');
    const ticketDetailSla = document.getElementById('ticketDetailSla');
    const ticketSlaPolicySection = document.getElementById('ticketSlaPolicySection');
    const ticketSlaPolicySelect = document.getElementById('ticketDetailSlaPolicySelect');
    const ticketSlaPolicySummary = document.getElementById('ticketDetailSlaPolicySummary');
    const ticketDetailCustomerName = document.getElementById('ticketDetailCustomerName');
    const ticketCustomerNameEditBtn = document.getElementById('ticketCustomerNameEditBtn');
    const ticketCustomerNameEditor = document.getElementById('ticketCustomerNameEditor');
    const ticketCustomerNameInlineInput = document.getElementById('ticketCustomerNameInput');
    const ticketCustomerNameSaveBtn = document.getElementById('ticketCustomerNameSaveBtn');
    const ticketCustomerNameCancelBtn = document.getElementById('ticketCustomerNameCancelBtn');
    const ticketCustomerEditBtn = document.getElementById('ticketCustomerEditBtn');
    const ticketCustomerEditDlg = document.getElementById('ticketCustomerEditDlg');
    const ticketCustomerEditForm = document.getElementById('ticketCustomerEditForm');
    const ticketCustomerEditClose = document.getElementById('ticketCustomerEditClose');
    const ticketCustomerEditCancel = document.getElementById('ticketCustomerEditCancel');
    const ticketCustomerEditSave = document.getElementById('ticketCustomerEditSave');
    const ticketCustomerEditStatus = document.getElementById('ticketCustomerEditStatus');
    const ticketCustomerEditName = document.getElementById('ticketCustomerEditName');
    const ticketCustomerEditPhone = document.getElementById('ticketCustomerEditPhone');
    const ticketCustomerEditEmail = document.getElementById('ticketCustomerEditEmail');
    const ticketCustomerEditRegion = document.getElementById('ticketCustomerEditRegion');
    const ticketCustomerEditCity = document.getElementById('ticketCustomerEditCity');
    const ticketCustomerEditAddress = document.getElementById('ticketCustomerEditAddress');
    const ticketDetailCustomer = document.getElementById('ticketDetailCustomer');
    const ticketBillingCard = document.getElementById('ticketBillingCard');
    const ticketBillingMeta = document.getElementById('ticketBillingMeta');
    const ticketBillingBalance = document.getElementById('ticketBillingBalance');
    const ticketBillingStateEl = document.getElementById('ticketBillingState');
    const ticketBillingNextDue = document.getElementById('ticketBillingNextDue');
    const ticketBillingSuspendedWrap = document.getElementById('ticketBillingSuspendedWrap');
    const ticketBillingSuspendedTill = document.getElementById('ticketBillingSuspendedTill');
    const ticketBillingConnections = document.getElementById('ticketBillingConnections');
    const ticketBillingSubscriptions = document.getElementById('ticketBillingSubscriptions');
    const ticketBillingUnsuspendBtn = document.getElementById('ticketBillingUnsuspendBtn');
    const ticketBillingRefreshBtn = document.getElementById('ticketBillingRefreshBtn');
    const ticketListToggle = document.getElementById('ticketListToggle');
    const ticketDetailDescription = document.getElementById('ticketDetailDescription');
    const ticketDescriptionExpandBtn = document.getElementById('ticketDescriptionExpand');
    const ticketDescriptionDlg = document.getElementById('ticketDescriptionDlg');
    const ticketDescriptionContent = document.getElementById('ticketDescriptionContent');
    const ticketPreviewLink = document.getElementById('ticketPreviewLink');
    const ticketPreviewDlg = document.getElementById('ticketPreviewDlg');
    const ticketPreviewContent = document.getElementById('ticketPreviewContent');
    const ticketMetaToggle = document.getElementById('ticketMetaToggle');
    const ticketDuplicateDlg = document.getElementById('ticketDuplicateDlg');
    // dailyStatus DOM refs (Dlg, Summary, Technicians, Refresh, Export,
    // Timestamp, Close) moved into daily-status.js (Phase 2.3.k.1).
    const ticketDuplicateForm = document.getElementById('ticketDuplicateForm');
    const ticketDuplicateStatus = document.getElementById('ticketDuplicateStatus');
    const ticketDuplicateList = document.getElementById('ticketDuplicateList');
    const ticketDuplicateConfirm = document.getElementById('ticketDuplicateConfirm');
    const ticketConflictDlg = document.getElementById('ticketConflictDlg');
    const ticketConflictTitle = document.getElementById('ticketConflictTitle');
    const ticketConflictMessage = document.getElementById('ticketConflictMessage');
    const ticketConflictList = document.getElementById('ticketConflictList');
    const ticketConflictClose = document.getElementById('ticketConflictClose');
    const ticketConflictResolve = document.getElementById('ticketConflictResolve');
    const ticketDetailMeta = document.getElementById('ticketDetailMeta');
    const ticketDetailTimeline = document.getElementById('ticketDetailTimeline');
    const ticketDetailActions = ticketsView ? ticketsView.querySelector('.ticket-detail-actions') : null;
    const ticketCloseTemplate = document.getElementById('ticketCloseTemplate');
    const ticketDeleteBtn = document.getElementById('ticketDeleteBtn');
    const ticketCommentsList = document.getElementById('ticketCommentsList');
    const ticketCommentsStatus = document.getElementById('ticketCommentsStatus');
    const ticketCommentForm = document.getElementById('ticketCommentForm');
    const ticketCommentSubmitBtn = ticketCommentForm ? ticketCommentForm.querySelector('button[type="submit"]') : null;
    const ticketCommentEditorEl = document.getElementById('ticketCommentEditor');
    const ticketCommentBody = document.getElementById('ticketCommentBody');
    const ticketCommentVisibility = document.getElementById('ticketCommentVisibility');
    const ticketCommentExpandBtn = document.getElementById('ticketCommentExpand');
    const ticketCommentExpandDlg = document.getElementById('ticketCommentExpandDlg');
    const ticketCommentExpandForm = document.getElementById('ticketCommentExpandForm');
    const ticketCommentExpandEditorEl = document.getElementById('ticketCommentExpandEditor');
    const ticketCommentExpandVisibility = document.getElementById('ticketCommentExpandVisibility');
    const ticketCommentExpandThread = document.getElementById('ticketCommentExpandThread');
    const ticketCommentExpandClose = document.getElementById('ticketCommentExpandClose');
    const ticketCommentExpandCancel = document.getElementById('ticketCommentExpandCancel');
    const ticketCommentExpandSubmit = document.getElementById('ticketCommentExpandSubmit');
    const TICKET_DESCRIPTION_EXPAND_THRESHOLD = 240;
    const PREVIEW_META_KEYS = new Set(['preview', 'message_preview']);
    const ticketCommentAttachmentList = document.getElementById('ticketCommentAttachmentList');
    const ticketCommentAttachmentModalList = document.getElementById('ticketCommentAttachmentModalList');
    const ticketCommentAttachmentAddBtn = document.getElementById('ticketCommentAttachmentAdd');
    const ticketCommentAttachmentModalAddBtn = document.getElementById('ticketCommentAttachmentModalAdd');
    const ticketCommentAttachmentInput = document.getElementById('ticketCommentAttachmentInput');
    const ticketDlg = document.getElementById('ticketDlg');
    const ticketForm = document.getElementById('ticketForm');
    // Technician (main) ticket-board DOM refs live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.1). `ticketsBoard`
    // itself is still resolved here because the `setTechniciansViewMode`
    // cluster (technicians/HR roster) toggles its `hidden` attribute when
    // switching between the table and board views; the rest of the board
    // DOM (lanes, supervisor filters, board-assign dialog) is internal to
    // `adminViews.tickets.technicianBoard`.
    const ticketsBoard = document.getElementById('ticketsBoard');
    const ticketsLayout = document.getElementById('ticketsLayout');
    // All `techReview*` DOM refs live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.e). Reach the dialog
    // via `adminViews.tickets.techReview.{open, close, ...}`.

    // Tech-closure review listeners are wired inside
    // `adminViews.tickets.techReview.mount()` (Phase 2.1.e).
    // Technician-board lane click + supervisor filters + board-assign
    // dialog listeners are wired inside
    // `adminViews.tickets.technicianBoard.mount()` (Phase 2.1.d.1).
    // Customer-board listeners are wired inside
    // `adminViews.tickets.customerBoard.mount()` (Phase 2.1.c).
    document.addEventListener('dragover', handleBoardDragAutoScroll);

    if (themeToggle) {
      themeToggle.addEventListener('click', () => {
        const current = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
        applyTheme(current === 'dark' ? 'light' : 'dark');
      });
      updateThemeToggle();
    }

    // Profile menu (`<details>` toggle) + profile/help item click listeners
    // + the outside-click closer all live in `src/lib/auth.js` (Phase 3.1.b).
    // Wired by `installAuthListeners()`.
    const ticketSubjectInput = document.getElementById('ticketSubject');
    const ticketPrioritySelect = document.getElementById('ticketPriority');
    const ticketComplaintSelect = document.getElementById('ticketComplaint');
    const ticketCustomerNameInput = document.getElementById('ticketCustomerName');
    const ticketCustomerPhoneInput = document.getElementById('ticketCustomerPhone');
    const ticketCustomerEmailInput = document.getElementById('ticketCustomerEmail');
    const ticketPrimaryContactSelect = document.getElementById('ticketPrimaryContact');
    const ticketDescriptionInput = document.getElementById('ticketDescription');
    const ticketAssignRoleCreate = document.getElementById('ticketAssignRoleCreate');
    const ticketAssignUserCreate = document.getElementById('ticketAssignUserCreate');
    const ticketAssignUserCreateHelp = document.getElementById('ticketAssignUserCreateHelp');
    const ticketInitialCommentInput = document.getElementById('ticketInitialComment');
    const ticketInitialCommentVisibility = document.getElementById('ticketInitialCommentVisibility');
    const ticketAssignUserCreateHelpDefault = ticketAssignUserCreateHelp ? ticketAssignUserCreateHelp.textContent : '';
    const techniciansTbody = document.getElementById('techniciansTbody');
    const addTechnicianBtn = document.getElementById('addTechnicianBtn');
    const technicianDlg = document.getElementById('technicianDlg');
    const technicianForm = document.getElementById('technicianForm');
    const technicianDlgTitle = document.getElementById('technicianDlgTitle');
    const technicianSubmitBtn = document.getElementById('technicianSubmitBtn');
    const technicianStatusDlg = document.getElementById('technicianStatusDlg');
    const technicianStatusForm = document.getElementById('technicianStatusForm');
    const technicianStatusName = document.getElementById('technicianStatusName');
    const technicianStatusSelect = document.getElementById('technicianStatusSelect');
    const technicianStatusReason = document.getElementById('technicianStatusReason');
    const technicianStatusTickets = document.getElementById('technicianStatusTickets');
    const technicianStatusEmpty = document.getElementById('technicianStatusEmpty');
    const technicianStatusTechList = document.getElementById('technicianStatusTechList');
    const technicianStatusSubmit = document.getElementById('technicianStatusSubmit');
    const technicianStatusCancel = document.getElementById('technicianStatusCancel');
    const technicianStatusClose = document.getElementById('technicianStatusClose');
    const ticketAssignDlg = document.getElementById('ticketAssignDlg');
    const ticketAssignForm = document.getElementById('ticketAssignForm');
    const ticketAssignUserSelect = document.getElementById('ticketAssignUser');
    const ticketAssignRoleSelect = document.getElementById('ticketAssignRole');
    const ticketAssignSummary = document.getElementById('ticketAssignSummary');
    const ticketAssignSubmitBtn = document.getElementById('ticketAssignSubmit');
    const ticketAssignNoteField = document.getElementById('ticketAssignNoteField');
    const ticketAssignNoteInput = document.getElementById('ticketAssignNote');
    const ticketAssignNoteHelp = document.getElementById('ticketAssignNoteHelp');
    const identifyCustomerBtn = document.getElementById('identifyCustomerBtn');
    const identifyCustomerHint = document.getElementById('identifyCustomerHint');
    const identifyCustomerDlg = document.getElementById('identifyCustomerDlg');
    const identifyCustomerForm = document.getElementById('identifyCustomerForm');
    const identifyCustomerStatus = document.getElementById('identifyCustomerStatus');
    const identifyCustomerList = document.getElementById('identifyCustomerList');
    const identifyCustomerEmpty = document.getElementById('identifyCustomerEmpty');
    const identifyCustomerSearchInput = document.getElementById('identifyCustomerSearch');
    const identifyCustomerSearchBtn = document.getElementById('identifyCustomerSearchBtn');
    const identifyCustomerConfirmBtn = document.getElementById('identifyCustomerConfirm');
    const identifyConnectionSection = document.getElementById('identifyConnectionSection');
    const identifyConnectionList = document.getElementById('identifyConnectionList');
    const identifyConnectionStatus = document.getElementById('identifyConnectionStatus');
    const ticketAssignTitle = document.getElementById('ticketAssignTitle');
    const ticketAssignUserHelp = document.getElementById('ticketAssignUserHelp');
    const ticketAssignRoleHelp = document.getElementById('ticketAssignRoleHelp');
    const ticketAssignRoleField = ticketAssignRoleSelect ? ticketAssignRoleSelect.closest('.field') : null;
    const technicianAdminUserSelect = document.getElementById('technicianAdminUser');
    const technicianDisplayNameInput = document.getElementById('technicianDisplayName');
    const technicianPhoneInput = document.getElementById('technicianPhone');
    const technicianSkillsInput = document.getElementById('technicianSkills');
    const technicianRegionsInput = document.getElementById('technicianRegions');
    const technicianActiveInput = document.getElementById('technicianActive');
    const pendingMessagesView = document.getElementById('pendingMessagesView');
    const pendingMessagesBody = document.getElementById('pendingMessagesBody');
    // Pending-messages dashboard DOM refs (18 elements — `pendingDashboard*`)
    // are resolved inside `frontend/admin/js/views/tickets.js` (Phase 2.1.b.1);
    // admin.js no longer touches them directly.
    // Duplicate Messages + Email Outbox + Tools-tab DOM refs and state
    // moved into `frontend/admin/js/views/{duplicate-messages,email-outbox}.js`.

    // Tools-view listener wiring (duplicates: refresh/delete/table; tools-tab
    // bar; email-outbox refresh; initial render of the duplicates table)
    // is wired by `adminViews.duplicateMessages.attachListeners()` and
    // `adminViews.emailOutbox.attachListeners()` from the boot block below.
    const pendingMessagesStatusFilter = document.getElementById('pendingMessagesStatusFilter');
    const pendingMessagesChannelFilter = document.getElementById('pendingMessagesChannelFilter');
    const pendingMessagesSearchInput = document.getElementById('pendingMessagesSearch');
    const pendingMessagesRefresh = document.getElementById('pendingMessagesRefresh');
    const pendingMessagesFooter = document.getElementById('pendingMessagesFooter');
    const pendingMessagesPageInfo = document.getElementById('pendingMessagesPageInfo');
    const pendingMessagesPrev = document.getElementById('pendingMessagesPrev');
    const pendingMessagesNext = document.getElementById('pendingMessagesNext');
    const pendingMessageDlg = document.getElementById('pendingMessageDlg');
    const pendingMessageForm = document.getElementById('pendingMessageForm');
    const pendingMessageTitle = document.getElementById('pendingMessageTitle');
    const pendingMessageMeta = document.getElementById('pendingMessageMeta');
    const pendingMessagePreview = document.getElementById('pendingMessagePreview');
    const pendingMessageAttachments = document.getElementById('pendingMessageAttachments');
    const pendingMessageSubjectInput = document.getElementById('pendingMessageSubject');
    const pendingMessagePrioritySelect = document.getElementById('pendingMessagePriority');
    const pendingMessageComplaintSelect = document.getElementById('pendingMessageComplaint');
    const pendingMessageLocationInput = document.getElementById('pendingMessageLocation');
    const pendingMessagePreferredChannelSelect = document.getElementById('pendingMessagePreferredChannel');
    const pendingMessageContactInput = document.getElementById('pendingMessageContact');
    const pendingMessageEmailInput = document.getElementById('pendingMessageEmail');
    const pendingMessageNameInput = document.getElementById('pendingMessageName');
    const pendingMessageDescriptionInput = document.getElementById('pendingMessageDescription');
    const pendingMessageCallbackField = document.getElementById('pendingMessageCallbackField');
    const pendingMessageCallbackConfirmed = document.getElementById('pendingMessageCallbackConfirmed');
    // Forward dialog DOM refs (4 elements) + most followup DOM refs are
    // resolved inside `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-fwd-followup).
    // `pendingMessageFollowupBtn` is kept here because the detail-dialog
    // code in admin.js (still 2.1.b.3-detail territory) toggles its
    // disabled state when a pending message is selected.
    const pendingMessageFollowupBtn = document.getElementById('pendingMessageFollowupBtn');
    // pendingMessageFollowup{Results,Status,SubmitBtn} live in views/tickets.js
    // (Phase 2.1.b.3-fwd-followup).
    const pendingMessageConvertBtn = document.getElementById('pendingMessageConvertBtn');
    const pendingMessageEnquiryBtn = document.getElementById('pendingMessageEnquiryBtn');
    const pendingMessageDiscardBtn = document.getElementById('pendingMessageDiscardBtn');
    const pendingMessageDiscardDlg = document.getElementById('pendingMessageDiscardDlg');
    const pendingMessageDiscardForm = document.getElementById('pendingMessageDiscardForm');
    const pendingMessageDiscardReason = document.getElementById('pendingMessageDiscardReason');
    const pendingMessageDiscardNote = document.getElementById('pendingMessageDiscardNote');
    const pendingMessageDiscardConfirm = document.getElementById('pendingMessageDiscardConfirm');
    const pendingMessageAssignRoleSelect = document.getElementById('pendingMessageAssignRole');
    const pendingMessageAssignUserSelect = document.getElementById('pendingMessageAssignUser');
    const pendingMessageAssignHelp = document.getElementById('pendingMessageAssignHelp');
    const pendingMessageAssignHelpDefault = pendingMessageAssignHelp ? pendingMessageAssignHelp.textContent : '';
    const pendingMessageEnquiryDlg = document.getElementById('pendingMessageEnquiryDlg');
    const pendingMessageEnquiryForm = document.getElementById('pendingMessageEnquiryForm');
    const pendingMessageEnquirySubmitBtn = document.getElementById('pendingMessageEnquirySubmit');
    const pendingMessageEnquiryStatus = document.getElementById('pendingMessageEnquiryStatus');
    const pendingEnquiryNameInput = document.getElementById('pendingEnquiryNameInput');
    const pendingEnquiryNameSuggestions = document.getElementById('pendingEnquiryNameSuggestions');
    const pendingEnquiryPhoneInput = document.getElementById('pendingEnquiryPhoneInput');
    const pendingEnquiryEmailInput = document.getElementById('pendingEnquiryEmailInput');
    const pendingEnquiryCityInput = document.getElementById('pendingEnquiryCityInput');
    const pendingEnquiryAddressInput = document.getElementById('pendingEnquiryAddressInput');
    const pendingEnquiryChannelSelect = document.getElementById('pendingEnquiryChannelSelect');
    const pendingEnquiryTypeSelect = document.getElementById('pendingEnquiryTypeSelect');
    const pendingEnquiryCustomerToggle = document.getElementById('pendingEnquiryCustomerToggle');
    const pendingEnquiryNotesInput = document.getElementById('pendingEnquiryNotesInput');
    const channelSettingsView = document.getElementById('channelSettingsView');
    const channelConfigsContainer = document.getElementById('channelConfigsContainer');
    const refreshConfigsBtn = document.getElementById('refreshConfigsBtn');
    const refreshTemplatesBtn = document.getElementById('refreshTemplatesBtn');
    const templateFilterChannel = document.getElementById('templateFilterChannel');
    const templateFilterEvent = document.getElementById('templateFilterEvent');
    const templateFilterLocale = document.getElementById('templateFilterLocale');
    const channelTemplatesTable = document.getElementById('channelTemplatesTable');
    const channelTemplatesTbody = document.getElementById('channelTemplatesTbody');
    const templateCreateForm = document.getElementById('templateCreateForm');
    const templateChannelInput = document.getElementById('templateChannel');
    const templateEventInput = document.getElementById('templateEvent');
    const templateLocaleInput = document.getElementById('templateLocale');
    const templateSubjectRow = document.getElementById('templateSubjectRow');
    const templateSubjectInput = document.getElementById('templateSubject');
    const templateBodyInput = document.getElementById('templateBody');
    const templateEnabledInput = document.getElementById('templateEnabled');
    const templateCreateSubmit = document.getElementById('templateCreateSubmit');
    const templatePreviewDlg = document.getElementById('templatePreviewDlg');
    const templatePreviewForm = document.getElementById('templatePreviewForm');
    const templatePreviewTitle = document.getElementById('templatePreviewTitle');
    const templatePreviewChannel = document.getElementById('templatePreviewChannel');
    const templatePreviewEvent = document.getElementById('templatePreviewEvent');
    const templatePreviewPlaceholders = document.getElementById('templatePreviewPlaceholders');
    const templatePreviewContext = document.getElementById('templatePreviewContext');
    const templatePreviewSubjectRow = document.getElementById('templatePreviewSubjectRow');
    const templatePreviewSubject = document.getElementById('templatePreviewSubject');
    const templatePreviewBody = document.getElementById('templatePreviewBody');
    const templateTestRecipientField = document.getElementById('templateTestRecipientField');
    const templateTestRecipientLabel = document.getElementById('templateTestRecipientLabel');
    const templateTestRecipient = document.getElementById('templateTestRecipient');
    const templateTestRecipientHint = document.getElementById('templateTestRecipientHint');
    const templatePreviewScenarioField = document.getElementById('templatePreviewScenarioField');
    const templatePreviewScenario = document.getElementById('templatePreviewScenario');
    const templateTestStatus = document.getElementById('templateTestStatus');
    const templateTestSendBtn = document.getElementById('templateTestSendBtn');
    const aiPromptsContainer = document.getElementById('aiPromptsContainer');
    const refreshPromptsBtn = document.getElementById('refreshPromptsBtn');
    const aiPromptHistoryDlg = document.getElementById('aiPromptHistoryDlg');
    const aiPromptHistoryTitle = document.getElementById('aiPromptHistoryTitle');
    const aiPromptHistoryList = document.getElementById('aiPromptHistoryList');
    const aiPromptHistoryStatus = document.getElementById('aiPromptHistoryStatus');
    // `ticketRegionForm` / `ticketRegionList` / `ticketRegionInput` DOM refs
    // are resolved inside `frontend/admin/js/views/tickets.js`; admin.js no
    // longer touches them directly.

    // `ticketListCollapsed` lives in `views.tickets.detail`. Read via
    // `ticketListCollapsed_get()`.

    // TICKET_STATUS_LABELS / TICKET_PRIORITY_LABELS / TICKET_ASSIGNEE_LABELS
    // live in `src/lib/ticket-utils.js` (Phase 3.1.a.3).
    const hasQuill = typeof window !== 'undefined' && typeof window.Quill !== 'undefined';
    const COMMENT_EDITOR_TOOLBAR = [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ];
    const COMMENT_EDITOR_OPTIONS = {
      theme: 'snow',
      modules: { toolbar: COMMENT_EDITOR_TOOLBAR },
      placeholder: 'Share an update for the team or customer…',
    };
    // `ticketCommentQuill` and `ticketCommentExpandQuill` live in
    // `views.tickets.detailComments`. Read via the shim below.
    // BILLING_COMPLAINT_TYPES / SALES_COMPLAINT_TYPES /
    // ESCALATION_SUPERVISOR_TARGETS / ASSIGNABLE_ROLE_NAMES /
    // SYSTEM_ADMIN_ROLE live in `src/lib/ticket-utils.js` (Phase 3.1.a.3).
    // TICKET_VISIBILITY_LABELS / COMMENT_REVIEW_LABELS / COMMENT_EDITABLE_STATES /
    // COMMENT_RETRY_STATES / COMMENT_OVERRIDE_STATES / COMMENT_SUPERVISOR_ROLES /
    // TICKET_CLOSE_TEMPLATES / TICKET_PRIORITY_OPTIONS / TICKET_COMPLAINT_OPTIONS /
    // ENQUIRY_TYPE_OPTIONS / CONTACT_METHOD_LABELS / CONTACT_METHOD_OPTIONS /
    // CHANNEL_PREFERRED_CONTACT / META_ORIGIN_ALIASES / ASSIGN_MODE_ASSIGN /
    // ASSIGN_MODE_ESCALATE all live in `src/lib/ticket-utils.js` (Phase 3.2.c).
    // CUSTOMER_CARE_ASSIGN_ROLES / CUSTOMER_CARE_SUPERVISOR_ROLE /
    // FIELD_TECH_ROLE / FIELD_TECH_SUPERVISOR_ROLE live there too (Phase 3.1.a.3).
    // SUPERVISOR_ROLE_DISPLAY / SUPERVISOR_ROLE_NAMES live there too (Phase 3.1.a.3).
    // PENDING_SLA_TARGET_MS / PENDING_MESSAGES_FETCH_LIMIT live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.2).

    // `ticketsInitialized`, `ticketsState`, `ticketAssigneeUserManual`, the
    // status/sort constants (`DEFAULT_CLOSED_FILTER`, `DEFAULT_TICKET_SORT`,
    // `TICKET_OPEN_STATUSES`, `TICKET_CLOSED_STATUSES`, `TICKET_PRIORITY_RANK`),
    // and the filter/sort helpers (`resolveTicketStatusFilters`,
    // `getTicketTimestamp`, `getTicketPriorityScore`, `sortTicketsForView`,
    // `resolveTicketAgeTime`, `isInstallationTicket`) all live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). Reach the list via
    // `adminViews.tickets.list.*`.


    // `ticketsReloadSeq`, `ticketsPendingSelectId`, `ticketsPendingFilterOverride`,
    // `ticketActionMenuEventsBound`, and `suppressNextTicketsReload` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). The few
    // remaining cross-cluster reads/writes (e.g. the assign dialog setting
    // `setSuppressNextReload`) reach into the module via accessors.

    // ---- Phase 2.1.d.2 thin shims --------------------------------------
    // The list module (`adminViews.tickets.list`) is the new home for the
    // ticket list, filter strip, pagination, and surrounding state. The
    // shims below preserve the original admin.js-level symbols so the
    // hundreds of call sites in this file (ticket actions menu, the
    // identify-customer flow, the ticket-comments cluster, the various
    // detail-panel reload triggers, etc.) keep working without a sweeping
    // sed pass. Each shim is a one-liner that delegates to the module —
    // they get inlined at every call site as 2.1.d.3 / 2.1.d.4 / 2.1.d.5
    // pull more code into the module.
    const __list = () => (window.adminViews && window.adminViews.tickets && window.adminViews.tickets.list);
    const ticketsState = new Proxy({}, {
      get(_, prop) { const m = __list(); return m ? m.getState()[prop] : undefined; },
      set(_, prop, value) { const m = __list(); if (m) m.getState()[prop] = value; return true; },
      has(_, prop) { const m = __list(); return m ? prop in m.getState() : false; },
      ownKeys() { const m = __list(); return m ? Reflect.ownKeys(m.getState()) : []; },
      getOwnPropertyDescriptor(_, prop) { const m = __list(); if (!m) return undefined; const v = m.getState()[prop]; return v !== undefined ? { value: v, writable: true, enumerable: true, configurable: true } : undefined; },
    });
    const unassignedState = new Proxy({}, {
      get(_, prop) { const m = __list(); return m ? m.getUnassignedState()[prop] : undefined; },
      set(_, prop, value) { const m = __list(); if (m) m.getUnassignedState()[prop] = value; return true; },
    });
    const reloadTickets = (opts) => { const m = __list(); return m ? m.refresh(opts) : Promise.resolve(); };
    const loadTicketDetailById = (id, opts) => { const m = __list(); return m ? m.loadTicketDetailById(id, opts) : Promise.resolve(null); };
    const openTicketFromNotification = (id) => { const m = __list(); if (m) m.openFromNotification(id); };
    const openTicketDetailAfterCreation = (id, payload) => { const m = __list(); return m ? m.openDetailAfterCreation(id, payload) : Promise.resolve(); };
    const selectTicket = (id, options) => { const m = __list(); if (m) m.selectTicket(id, options); };
    const findTicketById = (id) => { const m = __list(); return m ? m.findTicketById(id) : null; };
    const getSelectedTicket = () => { const m = __list(); return m ? m.getSelectedTicket() : null; };
    const getSelectedTicketIdValue = () => { const m = __list(); return m ? m.getSelectedTicketIdValue() : null; };
    const setTicketsViewMode = (mode) => { const m = __list(); if (m) m.setViewMode(mode); };
    const setTicketsQuickView = (mode) => { const m = __list(); if (m) m.setQuickView(mode); };
    const clearTicketsQuickMode = () => { const m = __list(); if (m) m.clearQuickMode(); };
    const resetTicketFiltersToDefault = (opts) => { const m = __list(); if (m) m.resetFiltersToDefault(opts); };
    const applyTicketsFilterOverride = (override, opts) => { const m = __list(); if (m) m.applyFilterOverride(override, opts); };
    const prepareTicketsFiltersForBoardTicket = (lane) => { const m = __list(); if (m) m.prepareFiltersForBoardTicket(lane); };
    const openTicketDetailFromBoard = (id, lane, opts) => { const m = __list(); return m ? m.openFromBoard(id, lane, opts) : Promise.resolve(); };
    const applyDefaultAssigneeUserFilter = (opts) => { const m = __list(); if (m) m.applyDefaultAssigneeUserFilter(opts); };
    const refreshUnassignedSnapshot = () => { const m = __list(); return m ? m.refreshUnassignedSnapshot() : Promise.resolve(); };
    const updateUnassignedIndicator = (hasAny) => { const m = __list(); if (m) m.updateUnassignedIndicator(hasAny); };
    const closeOpenTicketMenus = (except) => { const m = __list(); if (m) m.closeOpenMenus(except); };
    const resolveTicketStatusFilters = () => { const m = __list(); return m ? m.resolveTicketStatusFilters() : []; };
    const isInstallationTicket = (ticket) => { const m = __list(); return m ? m.isInstallationTicket(ticket) : false; };
    const resolveTicketAgeTime = (ticket) => { const m = __list(); return m ? m.resolveTicketAgeTime(ticket) : 0; };
    const sortTicketsForView = (items, mode) => { const m = __list(); return m ? m.sortTicketsForView(items, mode) : items; };
    function ticketsInitialized_get() { const m = __list(); return m ? m.isInitialized() : false; }
    function suppressNextTicketsReload_get() { const m = __list(); return m ? m.isSuppressNextReload() : false; }
    function suppressNextTicketsReload_set(v) { const m = __list(); if (m) m.setSuppressNextReload(v); }
    function ticketsPendingSelectId_set(v) { const m = __list(); if (m) m.setPendingSelectId(v); }
    function ticketAssigneeUserManual_get() { const m = __list(); return m ? m.isAssigneeUserManual() : false; }
    function ticketAssigneeUserManual_set(v) { const m = __list(); if (m) m.setAssigneeUserManual(v); }
    Object.defineProperty(window, '__list_shims_present__', { value: true });

    // `ticketAssignMode` and `ticketAssignmentCache` live in
    // `views.tickets.detailInteractions`. Reach via
    // `adminViews.tickets.detailInteractions.{getAssignMode, setAssignMode,
    // getAssignmentCache}`.
    // `slaPoliciesState` lives in `views.tickets.detail`.
    // `crmState`, `crmCustomerDetailCache`, `crmProspectDetailCache`,
    // `crmCustomerSearchTimer`, `crmProspectSearchTimer`, and `CRM_PAGE_SIZE`
    // live in `frontend/admin/js/views/crm.js` (Phase 2.2.c).
    // `crmInstallationsState`, `crmPlanCache`, and `crmBulkPoleDispatch`
    // live in `frontend/admin/js/views/crm-installations.js` (Phase 2.2.c.2).
    // `crmConfigState` and `crmConfigInitialized` live in
    // `frontend/admin/js/views/crm-config.js` (Phase 2.2.c.3).
    const CRM_PAGE_SIZE = 20;
    // Shim: legacy code reads `crmInstallationsState` directly (e.g. SSE
    // payloads, the `setCrmBlockingAlerts` flow, technician-roster cache
    // resets). Proxy through to the module's state so writes propagate.
    const crmInstallationsState = new Proxy({}, {
      get(_, prop) {
        const m = window.adminViews && window.adminViews.crmInstallations;
        if (!m) return undefined;
        const state = m.getState();
        const v = state[prop];
        return typeof v === 'function' ? v.bind(state) : v;
      },
      set(_, prop, value) {
        const m = window.adminViews && window.adminViews.crmInstallations;
        if (m) m.getState()[prop] = value;
        return true;
      },
    });
    // CRM customers/prospects shims so the leftover listener block at
    // ~15733 (and the view-router's `load:` callbacks) keep working until
    // the listener block is fully stripped below.
    const __crm = () => (window.adminViews && window.adminViews.crm);
    const loadCrmCustomers = () => { const m = __crm(); return m ? m.loadCustomers() : Promise.resolve(); };
    const loadCrmProspects = () => { const m = __crm(); return m ? m.loadProspects() : Promise.resolve(); };
    const openCrmProspectDialog = () => { const m = __crm(); if (m) m.openProspectDialog(); };
    const closeCrmProspectDialog = () => { const m = __crm(); if (m) m.closeProspectDialog(); };
    const formatCrmLabel = (v) => { const m = __crm(); return m ? m.formatLabel(v) : ''; };
    const crmStatusClass = (v) => { const m = __crm(); return m ? m.statusClass(v) : ''; };
    const truncate = (v, n) => { const m = __crm(); return m ? m.truncate(v, n) : (v == null ? '' : String(v)); };
    const crmState = new Proxy({}, {
      get(_, prop) { const m = __crm(); return m ? m.getState()[prop] : undefined; },
      set(_, prop, value) { const m = __crm(); if (m) m.getState()[prop] = value; return true; },
    });
    const crmCustomerDetailCache = new Proxy({}, {
      get(_, prop) {
        const m = __crm(); if (!m) return undefined;
        const cache = m.getCustomerDetailCache();
        const v = cache[prop];
        return typeof v === 'function' ? v.bind(cache) : v;
      },
    });
    const crmProspectDetailCache = new Proxy({}, {
      get(_, prop) {
        const m = __crm(); if (!m) return undefined;
        const cache = m.getProspectDetailCache();
        const v = cache[prop];
        return typeof v === 'function' ? v.bind(cache) : v;
      },
    });
    // `unassignedState` shim is declared above (Phase 2.1.d.2).
    // `pendingMessagesAlertActive` and `updatePendingMessagesIndicator`
    // live in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.4). Reach
    // via `adminViews.tickets.pendingMessages.list.{setIndicator,isAlertActive}`.

    // `updateUnassignedIndicator` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). The initial
    // false-default below is reset on the first `refreshUnassignedSnapshot`
    // call inside the list module, so we no longer need a boot-time
    // pre-clear here. Reach via `adminViews.tickets.list.*`.
    adminViews.tickets.pendingMessages.list.setIndicator(false);

    // technicianProfileByAdminId + technicianRosterLoaded/Loading state
    // moved into `src/lib/admin-roster.js` (Phase 3.2.c). Wire ticket-utils'
    // technician-profile lookup at the new owner.
    setTechnicianProfileLookup(getTechnicianProfileByAdminId);

    // `updateUnassignedIndicator`, `closeOpenTicketMenus`,
    // `handleTicketActionMenuOutsideClick`, `refreshUnassignedSnapshot`,
    // and `setTicketsViewMode` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). Reach via
    // `adminViews.tickets.list.{updateUnassignedIndicator, closeOpenMenus,
    // refreshUnassignedSnapshot, setViewMode}`.

    // Technician (main) ticket board lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.1). Reach it via
    // `adminViews.tickets.technicianBoard.{mount, refresh, refreshIfVisible,
    // scheduleRefresh, setStale, isStale, isInitialized, isVisible,
    // populateSupervisorFilter, focusLane, getState, ...}`.
    // The customer-care board (Phase 2.1.c) is reached via
    // `adminViews.tickets.customerBoard.*`.

    // `scheduleCustomerBoardRefresh` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.c). Reach via
    // `adminViews.tickets.customerBoard.scheduleRefresh()`.

    function handleTicketBoardDelta(data) {
      if (!data || typeof data !== 'object') return;
      const tb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
      const cb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.customerBoard;
      if (Object.prototype.hasOwnProperty.call(data, 'technician_lane') && tb) {
        tb.scheduleRefresh();
      }
      if (Object.prototype.hasOwnProperty.call(data, 'customer_care_lane') && cb) {
        cb.scheduleRefresh();
      }
    }
    // formatLaneCollapsedLabel lives in `src/lib/ticket-utils.js` (Phase 3.1.a.3).

    // The technician-board metric formatters, lane filters, supervisor
    // filter populator, supervisor-change handlers, and `focusTechnicianLane`
    // live in `frontend/admin/js/views/tickets.js` (Phase 2.1.d.1).
    // Reach `focusLane` via `adminViews.tickets.technicianBoard.focusLane(id)`;
    // `populateSupervisorFilter` via the same module.



    // ===== Technicians (Phase 2.3.i) chart + helpers =====
    // buildTechnicianInitials, hashTechColor, renderTechResolutionTrend,
    // loadTechResolutionTrend, setTechniciansViewMode moved into
    // `frontend/admin/js/views/technicians.js`.

    // `buildTicketsBoardCard` and `findBoardTicket` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.1).

    // `applyTicketsFilterOverride`, `prepareTicketsFiltersForBoardTicket`,
    // and `openTicketDetailFromBoard` (the cross-cluster nav helper) all
    // live in `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). Reach
    // via `adminViews.tickets.list.{applyFilterOverride,
    // prepareFiltersForBoardTicket, openFromBoard}`.

    // Technician-board click + drag handlers + lane drop +
    // `openBoardAssignDialog`/`closeBoardAssignDialog`/`handleBoardAssignSubmit`
    // live in `frontend/admin/js/views/tickets.js` (Phase 2.1.d.1).

    function handleBoardDragAutoScroll(event) {
      const tb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
      if (tb) {
        autoScrollBoardContainer(tb.getScrollEl(), tb.getBoardEl(), tb.getState(), event);
      }
      const cb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.customerBoard;
      if (cb) {
        autoScrollBoardContainer(cb.getScrollEl(), cb.getBoardEl(), cb.getState(), event);
      }
    }

    // Generic kanban-lane drag UX shared by both boards (technician +
    // customer). The technician-board module has its own copies of these
    // for its private listener wiring; admin.js exposes them as shared
    // helpers so the customer-board's `configure({helpers})` injection can
    // reuse the same behaviour without depending on the technician-board
    // module's internals.
    function handleBoardLaneDragOver(event) {
      event.preventDefault();
      const laneBody = event.currentTarget;
      laneBody.classList.add('dragover');
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    }

    function handleBoardLaneDragLeave(event) {
      event.currentTarget.classList.remove('dragover');
    }

    function autoScrollBoardContainer(scrollContainer, boardElement, state, event) {
      if (!scrollContainer || !boardElement || boardElement.hidden || boardElement.offsetParent === null) return;
      if (!state || !state.dragTicketId) return;
      const rect = scrollContainer.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom) return;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      if (maxScroll <= 0) return;
      const buffer = 120;
      const minStep = 12;
      const maxStep = 40;
      let delta = 0;
      const leftDiff = event.clientX - rect.left;
      const rightDiff = rect.right - event.clientX;
      if (leftDiff >= 0 && leftDiff < buffer && scrollContainer.scrollLeft > 0) {
        const factor = 1 - leftDiff / buffer;
        delta = -Math.max(minStep, Math.round(factor * maxStep));
      } else if (rightDiff >= 0 && rightDiff < buffer && scrollContainer.scrollLeft < maxScroll) {
        const factor = 1 - rightDiff / buffer;
        delta = Math.max(minStep, Math.round(factor * maxStep));
      }
      if (delta !== 0) {
        event.preventDefault();
        scrollContainer.scrollBy({ left: delta, behavior: 'auto' });
      }
    }

    // The board-assign dialog (`#boardAssignNoteDlg`) and its submit/close
    // handlers live in `frontend/admin/js/views/tickets.js` (Phase
    // 2.1.d.1). Outside callers reach `closeAssignDialog` via
    // `adminViews.tickets.technicianBoard.closeAssignDialog()`.









    // `applyQuickNavClasses`, `setTicketsQuickView`, `clearTicketsQuickMode`,
    // and `resetTicketFiltersToDefault` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). Reach via
    // `adminViews.tickets.list.{setQuickView, clearQuickMode, resetFiltersToDefault}`.
    // `ticketActivityState` and `ticketTimelineExpanded` live in
    // `views.tickets.detail`.
    let pendingMessagesInitialized = false;
    // `pendingMessagesState` (and the search-debounce timer) live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.2). Reach the list
    // via `adminViews.tickets.pendingMessages.list.*`.
    // `pendingDashboardState` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.1). Reach the
    // dashboard via `adminViews.tickets.pendingMessages.dashboard.*`.

    // `pendingForwardState` lives in `frontend/admin/js/views/tickets.js`
    // (Phase 2.1.b.3-fwd-followup). Reach the dialog via
    // `adminViews.tickets.pendingMessages.forward.*`.
    // `pendingEnquiryState` + `pendingEnquirySuggestionState` +
    // search-debounce timer live in `frontend/admin/js/views/tickets.js`
    // (Phase 2.1.b.3-detail).
    // `pendingFollowupState` (and the search-debounce timer) live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-fwd-followup).
    // Reach the dialog via `adminViews.tickets.pendingMessages.followup.*`.
    // `pendingMessageCurrent` migrated into
    // `frontend/admin/js/views/tickets.js` with the rest of the detail
    // dialog (Phase 2.1.b.3-detail). Reach it via
    // `adminViews.tickets.pendingMessages.detail.{getCurrent, setCurrent}`.
    // `pendingMessageDetailLoads` (in-flight detail-fetch dedup map) lives
    // in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.2).
    // `techniciansInitialized` moved into technicians.js (Phase 3.1) — under
    // ES modules each module has its own scope, so this can't be a shared
    // admin.js global anymore.
    // `customerBoardInitialized`, `customerBoardState`, `customerBoardDailyState`,
    // `customerBoardRefreshTimer`, `loadCustomerBoardCollapsed`, and
    // `persistCustomerBoardCollapsed` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.c). Reach via
    // `adminViews.tickets.customerBoard.*`.
    // techniciansViewMode + techniciansState moved into technicians.js (Phase 2.3.i).
    // dailyStatusState moved into daily-status.js (Phase 2.3.k.1).
    // `TICKETS_BOARD_COLLAPSE_STORAGE_KEY`, `ticketsBoardState`,
    // `technicianLaneHighlightTimer`, `ticketsBoardRefreshTimer`, and
    // `boardAssignState` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.1). Reach via
    // `adminViews.tickets.technicianBoard.*`.
    // techResolutionTrendState moved into technicians.js (Phase 3 follow-up).
    // `techReviewState` lives in `views.tickets.techReview` (Phase 2.1.e).
    const technicianStatusState = {
      technician: null,
      tickets: [],
      assignments: {},
      loadingTickets: false,
    };

    // `loadTicketsBoardCollapsed` and `persistTicketsBoardCollapsed` live
    // in `frontend/admin/js/views/tickets.js` (Phase 2.1.d.1).

    // ===== Daily Status (Phase 2.3.k.1) =====
    // Functions moved into `frontend/admin/js/views/daily-status.js`.
    // technicianEditId moved into technicians.js (Phase 2.3.i).
    // technicianAdminUsers / supervisorAdminUsers / techSupervisorAdminUsers
    // and their *Loaded/*Loading flags live in `src/lib/admin-roster.js`
    // (Phase 3.2.c). The body code below reaches for them via the imported
    // `getTechnicianAdminUsers()` / `getSupervisorAdminUsers()` / etc.
    // getters where needed.
    // `identifyState` lives in `views.tickets.detailInteractions`. The
    // remaining direct accesses (mostly inside the `identifyForm` submit
    // listener below) read through this Proxy.
    const identifyState = new Proxy({}, {
      get(_, prop) { const m = __int(); return m ? m.getIdentifyState()[prop] : undefined; },
      set(_, prop, value) { const m = __int(); if (m) m.getIdentifyState()[prop] = value; return true; },
    });

    // Logs view elements + state moved into
    // `frontend/admin/js/views/logs.js` (Phase 2.3.a).

    function sortNavGroupItems() {
      document.querySelectorAll('.nav-group-items').forEach(container => {
        // Containers that hold nested subgroups are hand-ordered — leave them
        // as authored so the subgroups keep their intended position. Only sort
        // a container's DIRECT .nav-view children (never descendants, which
        // would yank items out of nested groups).
        if (container.querySelector(':scope > .nav-group')) return;
        const items = Array.from(container.querySelectorAll(':scope > .nav-view'));
        items.sort((a, b) => {
          const labelA = a.querySelector('.label');
          const labelB = b.querySelector('.label');
          const textA = ((labelA ? labelA.textContent : '') || '').trim().toLowerCase();
          const textB = ((labelB ? labelB.textContent : '') || '').trim().toLowerCase();
          return textA.localeCompare(textB, undefined, { sensitivity: 'base' });
        });
        items.forEach(item => container.appendChild(item));
      });
    }
    sortNavGroupItems();

    const navViewButtons = document.querySelectorAll('.nav .nav-view');
    navViewButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.dataset.hrSection) {
          const m = window.adminViews && window.adminViews.hr;
          if (m) m.setSection(btn.dataset.hrSection);
        }
        if (btn.dataset.view === 'tickets') {
          if (btn.dataset.ticketQuick === 'unassigned') {
            setTicketsQuickView('unassigned');
          } else {
            setTicketsQuickView(null);
          }
          if (ticketListCollapsed_get()) {
            setTicketListCollapsed(false, { focusList: true });
          }
        }
        switchView(btn.dataset.view, btn);
      });
    });

    document.querySelectorAll('[data-view-jump]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const target = btn.dataset.viewJump;
        if (!target) return;
        const navTarget = Array.from(navViewButtons).find((navBtn) => navBtn.dataset.view === target);
        if (navTarget) {
          navViewButtons.forEach((navBtn) => navBtn.classList.remove('active'));
          navTarget.classList.add('active');
        }
        switchView(target, navTarget || null);
      });
    });

    document.querySelectorAll('[data-dialog-dismiss]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.dataset.dialogDismiss;
        if (!targetId) return;
        if (targetId === 'pendingMessageForwardDlg') {
          adminViews.tickets.pendingMessages.forward.close();
          return;
        }
        if (targetId === 'pendingMessageFollowupDlg') {
          adminViews.tickets.pendingMessages.followup.close();
          return;
        }
        if (targetId === 'ticketDuplicateDlg') {
            closeTicketDuplicateDialog();
            return;
          }
        if (targetId === 'shiftSetupDlg') {
          const sm = window.adminViews && window.adminViews.shiftManagement;
          if (sm) sm.setPromptDismissed(true);
        }
        const dialog = document.getElementById(targetId);
        if (!dialog) return;
        closeDialog(dialog);
      });
    });

    // shiftSetupDlg cancel/close listeners are wired by shift-management.js
    // mount() (Phase 2.3.k.2).

    // Profile dialog cancel/close listeners live in `src/lib/auth.js`
    // (Phase 3.1.b). Wired by `installAuthListeners()`.

    // Shift listeners wired by `adminViews.shiftManagement.mount()`.

    // Daily Status listeners wired by `adminViews.dailyStatus.attachListeners()`.
    if (ticketDescriptionExpandBtn && ticketDescriptionDlg && ticketDescriptionContent && ticketDetailDescription) {
      ticketDescriptionExpandBtn.addEventListener('click', () => {
        const fullText = ticketDetailDescription.dataset.fullText || '';
        ticketDescriptionContent.textContent = fullText || 'No description has been provided yet.';
        openDialog(ticketDescriptionDlg);
      });
    }

    if (ticketPreviewLink && ticketPreviewDlg && ticketPreviewContent) {
      ticketPreviewLink.addEventListener('click', () => {
        const previewText = ticketPreviewLink.dataset.preview || '';
        const plainPreview = toPlainText(previewText);
        ticketPreviewContent.textContent = plainPreview || '(no preview available)';
        openDialog(ticketPreviewDlg);
      });
    }
    if (ticketPreviewDlg) {
      ticketPreviewDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        try { ticketPreviewDlg.close(); }
        catch { ticketPreviewDlg.open = false; }
      });
    }

    if (ticketDetailMeta) {
      ticketDetailMeta.addEventListener('click', (event) => {
        const anchor = event.target && event.target.closest('[data-linked-ticket-id]');
        if (!anchor) return;
        event.preventDefault();
        const targetId = anchor.dataset.linkedTicketId;
        if (!targetId) return;
        const linkedCode = (anchor.dataset.linkedTicketShort || '').trim();
        if (linkedCode && ticketSearchInput) {
          ticketSearchInput.value = linkedCode;
        }
        ticketsState.filters.search = linkedCode;
        ticketsState.offset = 0;
        reloadTickets({ selectId: targetId, keepSelection: false }).catch(() => {});
      });
    }
    if (ticketMetaToggle && ticketDetailMeta) {
      ticketMetaToggle.addEventListener('click', () => {
        const willShow = ticketDetailMeta.hidden;
        setTicketMetaVisibility(willShow);
      });
    }



    // Customer-edit, duplicate, and conflict dialog listeners are wired
    // inside `adminViews.tickets.detailInteractions.mount()` (Phase 2.1.d.4).

    const navGroupToggles = document.querySelectorAll('.nav-group-toggle');
    navGroupToggles.forEach(toggle => {
      const groupName = toggle.dataset.groupToggle;
      const items = document.querySelector(`.nav-group-items[data-group="${groupName}"]`);
      const chevron = toggle.querySelector('.chevron');
      const setExpanded = (expanded) => {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (items) items.style.display = expanded ? '' : 'none';
        if (chevron) chevron.textContent = expanded ? '▾' : '▸';
      };
      setExpanded(toggle.getAttribute('aria-expanded') !== 'false');
      toggle.addEventListener('click', (evt) => {
        evt.preventDefault();
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        setExpanded(!expanded);
      });
    });

    function ensureAnalyticsIframeLoaded() {
      if (!analyticsIframe) return;
      const currentSrc = analyticsIframe.getAttribute('src');
      if (currentSrc && currentSrc !== 'about:blank') return;
      const targetSrc = analyticsIframe.dataset ? analyticsIframe.dataset.src : null;
      if (!targetSrc) return;
      analyticsIframe.setAttribute('src', targetSrc);
    }

	    const viewElements = {
	      analytics: analyticsView,
	      products: productsView,
	      users: usersView,
	      customers: customersView,
	      mobile_customers: mobileCustomersView,
	      access_points: accessPointsView,
	      hotspot_test: hotspotTestView,
	      mobilesessions: mobileSessionsView,
	      campaigns: campaignsView,
	      logs: logsView,
	      tickets: ticketsView,
      pending_messages: pendingMessagesView,
      customer_board: customerBoardView,
      tools: toolsView,
      technicians: techniciansView,
      hr: hrView,
      channel_settings: channelSettingsView,
      crm_customers: crmCustomersView,
      crm_reconciliation: crmReconciliationView,
      crm_retention: crmRetentionView,
      ess_portal: essPortalPanel,
      crm_prospects: crmProspectsView,
      crm_targets: crmTargetsView,
      crm_quote_approvals: crmQuoteApprovalsView,
      invoicing_review: invoicingReviewView,
      invoicing_reviews: invoicingReviewsView,
      invoicing_calendar: invoicingCalendarView,
      invoicing_taxation: invoicingTaxationView,
      crm_installations: crmInstallationsView,
      inventory: inventoryView,
      inventory_ledger: inventoryLedgerView,
      inventory_assets: inventoryAssetsView,
      inventory_purchasing: inventoryPurchasingView,
      infra_ops: infraOpsView,
      infra_team: infraTeamView,
      infra_facilities: infraFacilitiesView,
      infra_calendar: infraCalendarView,
      noc_outages: nocOutagesView,
      reports: reportsView,
      crm_config: crmConfigView,
      system_health: systemHealthView,
      security: securityView,
    };

    const controlElements = {
      addTicketBtn,
      shiftSetupBtn,
      dailyStatusBtn,
      addTechnicianBtn,
      addProductBtn,
      addUserBtn,
      scanApButton,
      customersToolbar,
    };

	    const viewConfigs = {
      analytics: {
        title: 'Analytics',
        show: ['analytics'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none' },
        load: () => ensureAnalyticsIframeLoaded(),
      },
      products: {
        title: 'Products',
        show: ['products'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: '', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.products;
          if (m) m.init();
        },
      },
      users: {
        title: 'Users',
        show: ['users'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: '', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.users;
          if (m) m.init();
        },
      },
      tickets: {
        title: 'Assigned Tickets',
        show: ['tickets'],
        controls: { addTicketBtn: '', shiftSetupBtn: '', dailyStatusBtn: '', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => initTicketsView(),
      },
      pending_messages: {
        title: 'New Messages',
        show: ['pending_messages'],
        controls: { addTicketBtn: 'none', shiftSetupBtn: '', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none' },
        load: () => initPendingMessagesView(),
      },
      customer_board: {
        title: 'Customer Ticket Board',
        show: ['customer_board'],
        controls: { addTicketBtn: 'none', shiftSetupBtn: '', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none' },
        load: () => window.adminViews.tickets.customerBoard.mount(),
      },
      tools: {
        title: 'Maintenance Tools',
        show: ['tools'],
        controls: { addTicketBtn: 'none', shiftSetupBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none' },
        load: () => {
          const dm = window.adminViews && window.adminViews.duplicateMessages;
          if (!dm) return;
          if (!dm.state.loaded) dm.load();
          else dm.render();
        },
      },
      technicians: {
        title: 'Tech Support',
        show: ['technicians'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addTechnicianBtn: '', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.technicians;
          if (m) m.init();
        },
      },
      hr: {
        title: 'HR',
        show: ['hr'],
        controls: { addTicketBtn: 'none', shiftSetupBtn: 'none', dailyStatusBtn: 'none', addTechnicianBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.hr;
          if (m) m.init();
        },
      },
      channel_settings: {
        title: 'Ticketing Configuration',
        show: ['channel_settings'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.channelSettings;
          if (m) m.init();
        },
      },
	      access_points: {
	        title: 'Access Points',
	        show: ['access_points'],
	        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: '', customersToolbar: 'none' },
	        load: () => { setAccessPointsTab('list'); },
	      },
	      hotspot_test: {
	        title: 'Hotspot Test',
	        show: ['hotspot_test'],
	        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
	        load: () => { /* iframe loads on demand */ },
	      },
      customers: {
        title: 'Customers',
        show: ['customers'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: '' },
        load: () => {
          const m = window.adminViews && window.adminViews.customers;
          if (m) m.init();
        },
      },
      crm_customers: {
        title: 'CRM – Customers',
        show: ['crm_customers'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => loadCrmCustomers(),
      },
      crm_reconciliation: {
        title: 'CRM – Reconciliation',
        show: ['crm_reconciliation'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.crmReconciliation;
          if (m) m.mount();
        },
      },
      crm_retention: {
        title: 'CRM – Retention',
        show: ['crm_retention'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.crmRetention;
          if (m) m.mount();
        },
      },
      ess_portal: {
        title: 'My HR',
        show: ['ess_portal'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.essPortal;
          if (m) m.mount();
        },
      },
      crm_prospects: {
        title: 'CRM – Prospects',
        show: ['crm_prospects'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => loadCrmProspects(),
      },
      crm_targets: {
        title: 'CRM – Targets',
        show: ['crm_targets'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.crm;
          if (m && typeof m.loadTargets === 'function') m.loadTargets();
        },
      },
      crm_quote_approvals: {
        title: 'CRM – Quote approvals',
        show: ['crm_quote_approvals'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.crmQuotes;
          if (m && typeof m.mountInbox === 'function') m.mountInbox();
        },
      },
      invoicing_review: {
        title: 'Billing – Invoice & receipt review',
        show: ['invoicing_review'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.invoicingReview;
          if (m && typeof m.mountReview === 'function') m.mountReview();
        },
      },
      invoicing_reviews: {
        title: 'Billing – Approvals (quotes & invoices)',
        show: ['invoicing_reviews'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.invoicingReviews;
          if (m && typeof m.mountReviews === 'function') m.mountReviews();
        },
      },
      invoicing_calendar: {
        title: 'Billing – Invoice reminder calendar',
        show: ['invoicing_calendar'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.invoicingCalendar;
          if (m && typeof m.mountCalendar === 'function') m.mountCalendar();
        },
      },
      invoicing_taxation: {
        title: 'Billing – Taxation (withholding tax)',
        show: ['invoicing_taxation'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.invoicingTaxation;
          if (m && typeof m.mountTaxation === 'function') m.mountTaxation();
        },
      },
      crm_installations: {
        title: 'CRM – Installations',
        show: ['crm_installations'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.crmInstallations;
          if (m) m.init();
        },
      },
      inventory: {
        title: 'Inventory – Stock',
        show: ['inventory'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.inventory;
          if (m) m.init();
        },
      },
      inventory_ledger: {
        title: 'Inventory – Movements',
        show: ['inventory_ledger'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.inventory; if (m) m.initLedger(); },
      },
      inventory_assets: {
        title: 'Inventory – Assets',
        show: ['inventory_assets'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.inventory; if (m) m.initAssets(); },
      },
      inventory_purchasing: {
        title: 'Inventory – Purchasing',
        show: ['inventory_purchasing'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.inventory; if (m) m.initPurchasing(); },
      },
      infra_ops: {
        title: 'Operations – Work Orders',
        show: ['infra_ops'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.infra; if (m) m.init(); },
      },
      infra_team: {
        title: 'Operations – Team',
        show: ['infra_team'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.infra; if (m) m.initTeam(); },
      },
      infra_facilities: {
        title: 'Operations – Facilities',
        show: ['infra_facilities'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.infra; if (m) m.initFacilities(); },
      },
      infra_calendar: {
        title: 'Operations – Maintenance',
        show: ['infra_calendar'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.infra; if (m) m.initCalendar(); },
      },
      noc_outages: {
        title: 'NOC Outages',
        show: ['noc_outages'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => { const m = window.adminViews && window.adminViews.noc; if (m) m.init(); },
      },
      crm_config: {
        title: 'CRM – Installation Config',
        show: ['crm_config'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.crmConfig;
          if (m) m.init();
        },
      },
      reports: {
        title: 'Reports',
        show: ['reports'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.reports;
          if (m) m.init();
        },
      },
      security: {
        title: 'Security',
        show: ['security'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.security;
          if (m) m.load();
        },
      },
      system_health: {
        title: 'System Health',
        show: ['system_health'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none', addTechnicianBtn: 'none', shiftSetupBtn: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.systemHealth;
          if (m) m.load();
        },
      },
      mobile_customers: {
        title: 'Mobile Customers',
        show: ['mobile_customers'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.mobileCustomers;
          if (m) m.setTab('customers');
        },
      },
      mobilesessions: {
        title: 'Mobile Sessions',
        show: ['mobilesessions'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.sessions;
          if (m) m.init();
        },
      },
      campaigns: {
        title: 'Campaigns',
        show: ['campaigns'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.campaigns;
          if (m) m.init();
        },
      },
      logs: {
        title: 'Logs',
        show: ['logs'],
        controls: { addTicketBtn: 'none', dailyStatusBtn: 'none', addProductBtn: 'none', addUserBtn: 'none', scanApButton: 'none', customersToolbar: 'none' },
        load: () => {
          const m = window.adminViews && window.adminViews.logs;
          if (m) m.init();
        },
      },
    };

    // Phase 3.2.c: views import `switchToView` from `src/lib/view-router.js`
    // and the lib delegates to this function via setViewRouter (called below).
    function switchView(view, btn) {
      let targetView = viewConfigs[view] ? view : 'analytics';
      let navBtn = btn || document.querySelector(`.nav .nav-view[data-view="${targetView}"]`);
      if (navBtn && navBtn.style.display === 'none') {
        targetView = 'analytics';
        navBtn = document.querySelector('.nav .nav-view[data-view="analytics"]');
      }
      document.querySelectorAll('.nav .nav-view').forEach(b => b.classList.remove('active'));
      if (navBtn) {
        navBtn.classList.add('active');
        // Expand every ancestor group (handles nested subgroups, e.g.
        // Admin → Configuration → Installation), not just the immediate one.
        let groupItems = navBtn.closest('.nav-group-items');
        while (groupItems) {
          const toggle = groupItems.previousElementSibling;
          if (toggle && toggle.classList.contains('nav-group-toggle')) {
            const chevron = toggle.querySelector('.chevron');
            toggle.setAttribute('aria-expanded', 'true');
            if (chevron) chevron.textContent = '▾';
            groupItems.style.display = '';
          }
          const parent = groupItems.parentElement;
          groupItems = parent ? parent.closest('.nav-group-items') : null;
        }
      }

      if (targetView === 'pending_messages') {
        adminViews.tickets.pendingMessages.list.setIndicator(false);
      }
      const config = viewConfigs[targetView] || viewConfigs.analytics;
      if (contentRoot) {
        if (targetView === 'analytics') {
          contentRoot.classList.add('analytics-active');
        } else {
          contentRoot.classList.remove('analytics-active');
        }
      }
      viewTitle.textContent = config.title;

      Object.values(viewElements).forEach(el => { if (el) el.style.display = 'none'; });
      (config.show || []).forEach(name => {
        const el = viewElements[name];
        if (el) el.style.display = '';
      });

      setTicketsViewMode('list');
      if (targetView === 'technicians') {
        const t = window.adminViews && window.adminViews.technicians;
        if (t) t.setViewMode(t.getViewMode() || 'board');
      }

      Object.entries(controlElements).forEach(([key, el]) => {
        if (!el) return;
        const hasControlValue = config.controls && Object.prototype.hasOwnProperty.call(config.controls, key);
        let value = hasControlValue ? config.controls[key] : (key === 'shiftSetupBtn' ? '' : 'none');
        if (key === 'shiftSetupBtn') {
          value = '';
        }
        if (key === 'addTicketBtn' && el.dataset.allowed === '0') {
          el.style.display = 'none';
        } else if (key === 'shiftSetupBtn') {
          el.style.display = el.dataset.allowed === '1' ? value : 'none';
        } else if (key === 'dailyStatusBtn') {
          el.style.display = el.dataset.allowed === '1' ? value : 'none';
        } else if (key === 'addTechnicianBtn' && el.style.display === 'none' && value !== 'none') {
          // honor gating applied in applyRoleGating
          el.style.display = el.dataset.allowed === '0' ? 'none' : value;
        } else {
          el.style.display = value;
        }
      });

      if (typeof config.load === 'function') {
        config.load();
      }

      // Phase 3.2.c: notify view modules that care about cross-cutting
      // topbar UI (e.g. campaigns shows/hides its "Add campaign" button
      // based on the active view). Replaces the legacy switchView monkey-
      // patch the campaigns module used to install via configure().
      try {
        document.dispatchEvent(new CustomEvent('admin:viewchange', {
          detail: { view: targetView },
        }));
      } catch {}
    }

    // Wire the view-router shim (Phase 3.2.c).
    setViewRouter(switchView);

    function initTicketsView() {
      if (!ticketsView) return;
      const list = adminViews.tickets.list;
      if (list && list.isInitialized()) {
        if (list.isSuppressNextReload()) {
          list.setSuppressNextReload(false);
        } else {
          list.refresh({ keepSelection: true });
        }
        return;
      }
      // Mark initialised in the module before we wire up the
      // detail-panel/comment/billing listeners — those listeners reference
      // module-owned state through the injected helpers, so the order
      // matters less than during the original boot, but staying consistent
      // with the pre-extraction sequence avoids surprising downstream
      // callers (the technicians cluster's `setTechniciansViewMode` reads
      // `tb.isInitialized()` to decide whether to refresh).
      if (list) list.setInitialized(true);

      const isRootUser = hasRole('root');
      if (ticketCustomerPhoneInput) {
        ticketCustomerPhoneInput.required = true;
        ticketCustomerPhoneInput.placeholder = 'e.g. 220123456';
      }

      // List-side filters/pagination/view-mode toggle + table click wire
      // up inside the module's mount() (idempotent). The board-mode
      // toggle still lives here because it switches into the technician
      // board cluster's flow.
      if (list) list.mount();

      // Create-ticket dialog form options are populated and its
      // listeners are wired inside `adminViews.tickets.create.mount()`
      // (Phase 2.1.d.2b).
      const create = adminViews.tickets && adminViews.tickets.create;
      if (create) {
        create.populateFormOptions();
        create.mount();
      }
      if (list) list.applyDefaultAssigneeUserFilter();
      ensureTechnicianUsersLoaded().then(() => {
        populateTicketAssigneeUserFilter();
        if (list && !list.isAssigneeUserManual()) {
          list.applyDefaultAssigneeUserFilter();
        }
      }).catch(() => {});
      // The comment editor (Quill) + comment list/visibility/expand
      // listeners + attachment buttons/lists are wired inside
      // `adminViews.tickets.detailComments.mount()` (Phase 2.1.d.5).
      const comments = adminViews.tickets && adminViews.tickets.detailComments;
      if (comments) comments.mount();
      if (ticketBillingRefreshBtn) {
        ticketBillingRefreshBtn.addEventListener('click', async () => {
          const ticket = getSelectedTicket();
          if (!ticket) return;
          const portaId = resolvePortaCustomerId(ticket);
          if (!portaId) {
            showFlash('Identify the customer before refreshing billing.', 'error');
            return;
          }
          ticketBillingRefreshBtn.disabled = true;
          await loadTicketBilling(ticket.id, { force: true });
        });
      }
      if (ticketBillingUnsuspendBtn) {
        ticketBillingUnsuspendBtn.addEventListener('click', async () => {
          const ticket = getSelectedTicket();
          if (!ticket) return;
          const portaId = resolvePortaCustomerId(ticket);
          if (!portaId) {
            showFlash('Identify the customer before lifting suspension.', 'error');
            return;
          }
          ticketBillingTicketId_set(ticket.id);
          ticketBillingUnsuspendBtn.disabled = true;
          try {
            const payload = await adminAPI.fetch(API.adminTicketBillingUnsuspend(ticket.id), { method: 'POST' });
            const __detailMod = adminViews.tickets.detail;
            if (__detailMod) __detailMod.setBillingState({ data: payload, loading: false, error: null });
            renderTicketBilling(payload);
            showFlash('Suspension temporarily lifted for 2 days.');
          } catch (err) {
            showFlash(err?.message || 'Failed to lift suspension', 'error');
          } finally {
            ticketBillingUnsuspendBtn.disabled = false;
          }
        });
      }
      // Comment visibility/expand listeners are wired inside
      // `adminViews.tickets.detailComments.mount()` (Phase 2.1.d.5).
      setTicketListCollapsed(false);
      if (ticketListToggle) {
        ticketListToggle.addEventListener('click', () => {
          const next = !ticketListCollapsed_get();
          setTicketListCollapsed(next, { focusList: !next });
        });
      }
      // Ticket-row click handler is wired inside the list module's mount().
      // Comment form + expand form + attachment listeners are wired inside
      // `adminViews.tickets.detailComments.mount()` (Phase 2.1.d.5).
      // Ticket-action menu, assign dialog, identify-customer dialog, and
      // SLA-policy change listener are wired inside
      // `adminViews.tickets.detailInteractions.mount()` (Phase 2.1.d.4).
      const interactions = adminViews.tickets && adminViews.tickets.detailInteractions;
      if (interactions) interactions.mount();

      const detail = adminViews.tickets && adminViews.tickets.detail;
      if (detail) detail.ensureSlaPoliciesLoaded().catch(() => {});

      const pending = list ? list.getPendingFilterOverride() : null;
      if (list) {
        if (pending) {
          list.applyFilterOverride(pending, { skipQuickMode: true, syncControls: true, preserve: false });
        } else {
          list.applyDefaultAssigneeUserFilter({ force: true });
        }
        if (list.isSuppressNextReload()) {
          list.setSuppressNextReload(false);
        } else {
          list.refresh();
        }
      }
    }

    // `reloadTickets`, `loadTicketDetailById`, `openTicketFromNotification`,
    // `openTicketDetailAfterCreation`, `renderTicketsTable`,
    // `updateTicketSelectionHighlight`, `selectTicket`, `findTicketById`,
    // `getSelectedTicket` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). Reach via
    // `adminViews.tickets.list.{refresh, loadTicketDetailById,
    // openFromNotification, openDetailAfterCreation, selectTicket,
    // findTicketById, getSelectedTicket}`.















    // `populateTicketFormOptions`, `populateTicketCreateAssignmentOptions`,
    // `openTicketDialog`, `resetTicketForm`, `updatePrimaryContactRequirement`,
    // and `handleTicketCreateSubmit` live in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2b). Reach via
    // `adminViews.tickets.create.{open, reset, populateFormOptions,
    // populateAssignmentOptions, updatePrimaryContactRequirement, mount}`.

    // populateAssignmentOptions moved into tickets.js (Phase 3.2.c). admin.js
    // no longer needs it — admin-roster.js's loaders call into tickets sub-
    // views via `views.tickets.X.populateAssignmentOptions()` directly.

    // `populatePendingMessageAssignmentOptions` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // (Earlier duplicate `function parseListInput(value)` at line 3747 removed
    // during Phase 3.1 — script-tag mode silently shadowed the more-capable
    // definition at line 1778. ES-module strict mode rejects the duplicate.)

    // updateTechnicianProfiles lives in `src/lib/admin-roster.js` (Phase 3.2.c).

    // `updatePrimaryContactRequirement` lives in `views.tickets.create`.

    // fallbackAdminContact / deriveAdminDisplayName / primaryAdminUserName /
    // adminUserSortKey / contactIsRedundant / formatAdminUserLabel /
    // formatAssignmentOptionLabel all live in `src/lib/ticket-utils.js`
    // (Phase 3.1.a.3). deriveAdminDisplayName reads the live
    // technicianProfileByAdminId Map via the thunk wired up at boot.

    // populateTicketAssigneeUserFilter moved into tickets.js (Phase 3.2.c).
    const populateTicketAssigneeUserFilter = (selectedValue) => {
      const list = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.list;
      if (list && typeof list.populateAssigneeUserFilter === 'function') {
        list.populateAssigneeUserFilter(selectedValue);
      }
    };

    // `applyDefaultAssigneeUserFilter` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). Reach via
    // `adminViews.tickets.list.applyDefaultAssigneeUserFilter()`.

    // ensureTechnicianRosterLoaded / ensureTechnicianUsersLoaded /
    // ensureSupervisorUsersLoaded / ensureTechSupervisorUsersLoaded all
    // live in `src/lib/admin-roster.js` (Phase 3.2.c). admin.js imports
    // them above. The lib module reaches view modules' populate*
    // callbacks (populateAssignmentOptions, populateSupervisorFilter,
    // populateAdminUserOptions) lazily via window.adminViews so it
    // doesn't import its sibling views.
    //
    // Note: tickets.list's populateTicketAssigneeUserFilter is exposed
    // as `populateAssigneeUserFilter` on the list view. The lib calls
    // through that surface; admin.js's local
    // `populateTicketAssigneeUserFilter` shim defined further down is
    // unused once tickets.list converts in 3.2.c part 3.










    // extractRoleNames / userIsSupervisor / derivePrimaryAssignmentRole /
    // describeSupervisorTargets / deriveSupervisorAdminRoles /
    // userEligibleForTicketAssignment all live in
    // `src/lib/ticket-utils.js` (Phase 3.1.a.3). userEligibleForTicketAssignment
    // reads the live ADMIN_ME via the thunk wired up at boot.

    // ===== Technicians (Phase 2.3.i) form helpers =====
    // userHasTechnicianRole, populateTechnicianAdminUserOptions,
    // resetTechnicianForm moved into technicians.js.

    // hydrateTicketAssignment moved into tickets.js (Phase 3.2.c) — the
    // admin.js copy was latent dead code that referenced
    // `ticketAssignmentCache` which lives only in tickets.js.



































    // The pending-messages list/filters/pagination layer (loadPendingMessages,
    // changePendingMessagesPage, isPendingMessagesViewActive, normalize* helpers,
    // upsert/remove/fetch helpers, renderPendingMessages, age-indicator refresh)
    // lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.2). Reach it
    // via `adminViews.tickets.pendingMessages.list.*`.

    // Forward dialog (ensureForwardUsersLoaded, populatePendingForwardUsers,
    // openPendingMessageForwardDialog, closePendingMessageForwardDialog,
    // handlePendingMessageForwardSubmit) lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-fwd-followup).
    // Reach via `adminViews.tickets.pendingMessages.forward.{ open, close, mount }`.


    // Followup dialog (setPendingMessageFollowupStatus, reset/render/load/open/close,
    // handlePendingMessageFollowupSubmit) lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-fwd-followup).
    // Reach via `adminViews.tickets.pendingMessages.followup.{ open, close, mount }`.

    // `resetPendingMessageEnquiryDialog` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `closePendingMessageEnquiryDialog` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `populatePendingEnquiryFields` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).
    // `setPendingEnquiryContactCategory` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).
    // `normalizePhoneDigits` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `resolveContactLabel` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `pickBestCustomerMatch` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `pickBestProspectMatch` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `schedulePendingEnquiryAutoDetect` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).
    // `autoDetectPendingEnquiryContact` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).
    // `ensurePendingEnquiryTypeOptions` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `clearPendingEnquirySuggestions` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `renderPendingEnquirySuggestions` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `loadPendingEnquirySuggestions` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `hidePendingEnquirySuggestions` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `selectPendingEnquirySuggestion` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `handlePendingEnquiryNameInput` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `handlePendingEnquiryNameKeyDown` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `openPendingMessageEnquiryDialog` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `handlePendingMessageEnquirySubmit` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `handlePendingMessageFollowupSearchInput` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-fwd-followup).

    // `isPendingMessageFormDirty` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `markPendingMessageDirty` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `resetPendingMessageDirty` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `pendingMessageRequiresCallback` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `updatePendingMessageConvertState` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `openPendingMessageDialog` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).
    // `extractRecordingHintFromPayload` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `getPendingMessageRecordingHint` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `applyPendingMessageUpdate` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `fetchPendingMessageRecording` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `populatePendingMessageDetail` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `prefillPendingMessageContactContext` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `submitPendingMessageConversion` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `buildPendingMessageDiscardUrl` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `openPendingMessageDiscardDialog` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `handlePendingMessageDiscardConfirm` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // `handlePendingMessagesTableClick` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.b.2).

    // `handlePendingMessageDialogClick` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    // ===== Duplicate Messages (Phase 2.3.j.5) =====
    // Functions moved into `frontend/admin/js/views/duplicate-messages.js`.

    // ===== Email outbox + Tools tab (Phase 2.3.j.6) =====
    // Functions moved into `frontend/admin/js/views/email-outbox.js`.

    function initPendingMessagesView() {
      if (!pendingMessagesView) return;
      if (!pendingMessagesInitialized) {
        pendingMessagesInitialized = true;
        // The list/dashboard/forward/followup/detail sub-views all live in
        // `frontend/admin/js/views/tickets.js`. Each mount() is idempotent.
        adminViews.tickets.pendingMessages.list.mount();
        adminViews.tickets.pendingMessages.dashboard.mount();
        adminViews.tickets.pendingMessages.forward.mount();
        adminViews.tickets.pendingMessages.followup.mount();
        adminViews.tickets.pendingMessages.detail.mount();
      }
      adminViews.tickets.pendingMessages.list.refresh({ skipIfFresh: true });
      adminViews.tickets.pendingMessages.dashboard.refresh({ skipIfFresh: true }).catch(() => {});
    }


    // describeDuration lives in `src/lib/ticket-utils.js` (Phase 3.1.a.3).




    // renderHtmlOrText lives in `src/lib/format.js` (Phase 3.2.c).






    // formatAiRecommendations lives in `src/lib/ticket-utils.js` (Phase 3.1.a.3).

















    // ===== Technicians (Phase 2.3.i) bulk =====
    // openTechnicianDialog, loadTechnicians, renderTechnicians, status
    // dialog, drag-and-drop handlers, handleTechnicianFormSubmit,
    // initTechniciansView, etc. moved into
    // `frontend/admin/js/views/technicians.js`. The view-router calls
    // `adminViews.technicians.init()`.

    // HR cluster (~5 200 lines: shift template helpers, loaders,
    // section-switching, the entire `initShiftTemplatesView` listener-
    // wiring monolith, and every dialog dispatcher) moved into
    // `frontend/admin/js/views/hr.js` (Phase 2.2.d). The view router
    // calls `adminViews.hr.init()` instead of the previous
    // `initShiftTemplatesView()` call.

    // ===== Channel Settings (Phase 2.3.j.4) =====
    // Functions moved into `frontend/admin/js/views/channel-settings.js`.
    // getChannelLabel + CHANNEL_LABEL_MAP live in `src/lib/channels.js`
    // (Phase 3.2.c).

    // ===== AI Prompts (Phase 2.3.j.1) =====
    // Functions moved into `frontend/admin/js/views/ai-prompts.js`.

    // ===== Message Templates (Phase 2.3.j.2) =====
    // Functions moved into `frontend/admin/js/views/message-templates.js`.




    // `getSelectedTicketIdValue` lives in
    // `frontend/admin/js/views/tickets.js` (Phase 2.1.d.2). Reach via
    // `adminViews.tickets.list.getSelectedTicketIdValue()`.
























    // formatTicketStatus / formatTicketComplaint / formatTicketPriority /
    // formatTicketAssignee live in `src/lib/ticket-utils.js` (Phase 3.1.a.3).

    // performLogout + logoutBtn click listener live in `src/lib/auth.js`
    // (Phase 3.1.b). Wired by `installAuthListeners()`.

    // ===== Products (Phase 2.3.e) =====
    // DOM refs, state, render/load/openProductDialog and listener wiring
    // moved into `frontend/admin/js/views/products.js`. The view-router
    // calls `adminViews.products.init()`.

    // ===== Sessions + diagnostics (Phase 2.3.h) =====
    // AP Sessions, Grace Sessions, AuthMon stats, Mobile Sessions, and the
    // Hotspot Test (E2E) tool moved into
    // `frontend/admin/js/views/sessions.js`. The Access Points "sessions"
    // sub-tab calls `adminViews.sessions.{loadAPSessions,loadGraceSessions,loadAuthmonStats}`.
    // The `mobilesessions` view-router entry calls
    // `adminViews.sessions.loadMobileSessions()`.

    // ===== Access Points (Phase 2.3.d) =====
    // DOM refs, state, render/load/openApDialog/openApDistributorDialog
    // and listener wiring moved into
    // `frontend/admin/js/views/access-points.js`. The view-router calls
    // `adminViews.accessPoints.init()`.
	    // ===== Customers / Distributors tabs =====
	    let customersActiveTab = 'customers';

	    function setCustomersTab(tab) {
	      customersActiveTab = tab === 'distributors' ? 'distributors' : 'customers';
	      if (customersTabBtnCustomers) customersTabBtnCustomers.classList.toggle('primary', customersActiveTab === 'customers');
	      if (customersTabBtnDistributors) customersTabBtnDistributors.classList.toggle('primary', customersActiveTab === 'distributors');
	      if (customersTabCustomersWrap) customersTabCustomersWrap.style.display = customersActiveTab === 'customers' ? '' : 'none';
	      if (customersTabDistributorsWrap) customersTabDistributorsWrap.style.display = customersActiveTab === 'distributors' ? '' : 'none';
	      if (customersActiveTab === 'distributors') loadDistributors();
	    }

	    if (customersTabBtnCustomers) customersTabBtnCustomers.addEventListener('click', () => setCustomersTab('customers'));
	    if (customersTabBtnDistributors) customersTabBtnDistributors.addEventListener('click', () => setCustomersTab('distributors'));

	    // Distributors view (state, cache, render, load, dialog, detail,
	    // manual top-up, listener wiring) lives in
	    // `frontend/admin/js/views/distributors.js` (Phase 2.2.a). Reach via
	    // `adminViews.distributors.{mount, refresh, ensureCache, getCache,
	    // openDialog, openDetail, formatMoney}`.
	    const __dist = () => (window.adminViews && window.adminViews.distributors);
	    const ensureDistributorCache = () => { const m = __dist(); return m ? m.ensureCache() : Promise.resolve({ byId: new Map(), items: [] }); };
	    const loadDistributors = (q) => { const m = __dist(); return m ? m.refresh(q) : Promise.resolve(); };
	    const openDistributorDialog = (id) => { const m = __dist(); return m ? m.openDialog(id) : Promise.resolve(); };
	    const openDistributorDetail = (id, opts) => { const m = __dist(); return m ? m.openDetail(id, opts) : Promise.resolve(); };
	    const formatMoney = (amount, currency) => { const m = __dist(); return m ? m.formatMoney(amount, currency) : ''; };
	    const distributorCache = new Proxy({}, {
	      get(_, prop) { const m = __dist(); if (!m) return undefined; const cache = m.getCache(); const v = cache[prop]; return typeof v === 'function' ? v.bind(cache) : v; },
	    });

    // ===== Users (Phase 2.3.c) =====
    // DOM refs, state, render/load/openUserDialog/openUserRolesDialog
    // and listener wiring moved into
    // `frontend/admin/js/views/users.js`. The view-router calls
    // `adminViews.users.init()`.

    // ===== Customers (Phase 2.3.b) =====
    // DOM refs, state, render/load/openCustomerDetail and listener wiring
    // moved into `frontend/admin/js/views/customers.js`. The view-router
    // calls `adminViews.customers.init()`.

    // CRM customers/prospects listeners are wired inside
    // `adminViews.crm.mount()` (Phase 2.2.c).

    // CRM installations cluster (constants, state, helpers, dialogs,
    // detail panel, list/dashboard loaders, plan-cache helpers, the
    // bulk pole/note printers, and the technician-roster prime call)
    // moved into `frontend/admin/js/views/crm-installations.js`
    // (Phase 2.2.c.2). The view router calls
    // `adminViews.crmInstallations.init()` instead of the previous
    // `initCrmInstallationsView()`.
    // `reportsInitialized` and `initReportsView` moved into
    // `frontend/admin/js/views/reports.js` (Phase 2.3.g). The
    // `openCrmInstallReport` DOM ref it referenced had previously moved
    // to crm-installations.js (Phase 2.2.c.2) — leaving initReportsView
    // with a dangling reference that would have thrown at first open.

    // openDialog / closeDialog live in `src/lib/dialog.js` (Phase 3.2.c).

    // Profile/password form submit handler lives in `src/lib/auth.js`
    // (Phase 3.1.b). Wired by `installAuthListeners()`.

    // Pending-message enquiry button + form listeners are owned by
    // `adminViews.tickets.pendingMessages.detail` (Phase 2.1.b.3-detail).

    if (ticketSubjectEditBtn) {
      ticketSubjectEditBtn.addEventListener('click', () => {
        const ticket = getSelectedTicket();
        const value = ticket ? (ticket.subject || '') : (ticketSubjectEditor ? ticketSubjectEditor.dataset.value || '' : '');
        openTicketSubjectEditor(value);
      });
    }
    if (ticketSubjectCancelBtn) {
      ticketSubjectCancelBtn.addEventListener('click', () => {
        const value = ticketSubjectEditor ? ticketSubjectEditor.dataset.value || '' : '';
        resetTicketSubjectEditor(value);
      });
    }
    if (ticketSubjectSaveBtn) {
      ticketSubjectSaveBtn.addEventListener('click', submitTicketSubject);
    }
    if (ticketSubjectInlineInput) {
      ticketSubjectInlineInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submitTicketSubject();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          const value = ticketSubjectEditor ? ticketSubjectEditor.dataset.value || '' : '';
          resetTicketSubjectEditor(value);
        }
      });
    }

    if (ticketComplaintEditBtn) {
      ticketComplaintEditBtn.addEventListener('click', () => {
        const ticket = getSelectedTicket();
        const value = ticket ? (ticket.complaint_type || '') : (ticketComplaintEditor ? ticketComplaintEditor.dataset.value || '' : '');
        openTicketComplaintEditor(value);
      });
    }
    if (ticketComplaintCancelBtn) {
      ticketComplaintCancelBtn.addEventListener('click', () => {
        const value = ticketComplaintEditor ? ticketComplaintEditor.dataset.value || '' : '';
        resetTicketComplaintEditor(value);
      });
    }
    if (ticketComplaintSaveBtn) {
      ticketComplaintSaveBtn.addEventListener('click', submitTicketComplaint);
    }
    if (ticketComplaintInput) {
      ticketComplaintInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submitTicketComplaint();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          const value = ticketComplaintEditor ? ticketComplaintEditor.dataset.value || '' : '';
          resetTicketComplaintEditor(value);
        }
      });
    }

    if (ticketCustomerNameEditBtn) {
      ticketCustomerNameEditBtn.addEventListener('click', () => {
        const ticket = getSelectedTicket();
        if (!ticket || !ticket.support_customer_id) {
          showFlash('Link a customer before editing the name.', 'error');
          return;
        }
        const value = (ticket.support_customer && ticket.support_customer.display_name) || (ticketCustomerNameEditor ? ticketCustomerNameEditor.dataset.value || '' : '');
        openTicketCustomerNameEditor(value);
      });
    }
    if (ticketCustomerNameCancelBtn) {
      ticketCustomerNameCancelBtn.addEventListener('click', () => {
        const ticket = getSelectedTicket();
        const canEdit = Boolean(ticket && ticket.support_customer_id);
        const value = ticketCustomerNameEditor ? ticketCustomerNameEditor.dataset.value || '' : '';
        resetTicketCustomerNameEditor(value, canEdit);
      });
    }
    if (ticketCustomerNameSaveBtn) {
      ticketCustomerNameSaveBtn.addEventListener('click', submitTicketCustomerName);
    }
    if (ticketCustomerNameInlineInput) {
      ticketCustomerNameInlineInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submitTicketCustomerName();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          const ticket = getSelectedTicket();
          const canEdit = Boolean(ticket && ticket.support_customer_id);
          const value = ticketCustomerNameEditor ? ticketCustomerNameEditor.dataset.value || '' : '';
          resetTicketCustomerNameEditor(value, canEdit);
        }
      });
    }

    // Pending-enquiry name typeahead + customer-toggle + phone/email
    // auto-detect listeners are owned by
    // `adminViews.tickets.pendingMessages.detail` (Phase 2.1.b.3-detail).

    // ===== Search filter (client-side) =====

    // ===== Campaigns (Phase 2.3.f) =====
    // DOM refs, state, render/load/openCampaignDialog/openVideoDialog
    // and listener wiring moved into
    // `frontend/admin/js/views/campaigns.js`. The view-router calls
    // `adminViews.campaigns.init()`. The module also wraps `switchView`
    // in mount() to manage the topbar "Add campaign" button.
    // ===== Login flow =====
    // handleLoginSubmit + login form/keydown/submit-button wiring live in
    // `src/lib/auth.js` (Phase 3.1.b). Reach via `installAuthListeners()`.

    // ===== Helpers =====


    // updateSessionStatus lives in `src/lib/auth.js` (Phase 3.1.b).

    // ===== Init =====
    // Wire login/profile/dialog event listeners now that all DOM refs
    // declared above (`viewElements`, `analyticsIframe`, `openDialog`,
    // etc.) have resolved and the function declarations referenced by
    // `configureAuth` are in scope.
    installAuthListeners();
    (async function init(){
      updateSessionStatus();
      // Load locale/timezone/currency before the first view renders so every
      // formatter (dates, currency, numbers) follows the configured settings.
      await loadFormattingConfig();
      // An emailed reset link lands here with `?reset_token=`. It takes
      // precedence over the session gate: somebody arriving on that link is
      // recovering an account, and an existing session (or a stale one) must
      // not swallow the token before the overlay can use it.
      if (consumeResetTokenFromUrl()) return;
      const ok = await gate();
      if (!ok) return; // login overlay shown
      // initial load
      switchView('analytics');
      refreshUnassignedSnapshot();
    })();
    // ===== Logs (Phase 2.3.a) =====
    // Functions and listener wiring moved into
    // `frontend/admin/js/views/logs.js`. The view-router calls
    // `adminViews.logs.init()`.
